/**
 * AwsCostDashboard.jsx
 * Self-contained — tidak perlu file komponen terpisah.
 * Menerima prop: token (JWT string dari parent/context)
 * Endpoint: /api/aws/data, /api/aws/upload, /api/aws/years
 *
 * Dependencies: react, chart.js, react-chartjs-2
 *   npm install chart.js react-chartjs-2
 *
 * ── CHANGELOG ──────────────────────────────────────────────────────────────
 * 1. Year selector dipindah dari header (DashHeader) ke baris tab bar (kanan).
 *    Tombol tahun + Reset + hint "Ctrl+klik multi" sekarang sejajar dengan tab.
 * 2. Header kedua "AWS Cost Dashboard" (DashHeader) dihapus — dobel dgn header utama.
 * 3. Anti-kedip saat ganti tahun: flag firstLoad/refreshing. Spinner besar hanya
 *    saat load pertama; saat ganti tahun, dashboard lama tetap tampil (diredupkan
 *    halus) + indikator spinner kecil. Tab & year selector tetap interaktif.
 * 4. Pie chart tab Consolidated diperbaiki ukurannya (maxWidth 360px, di-center)
 *    agar proporsional dengan bar chart di sebelahnya.
 * 5. Legend pie chart Consolidated dipindah ke atas (position:'top') biar seragam
 *    dengan bar chart.
 * 6. Tombol "All" ditambahkan di year selector untuk memilih semua tahun sekaligus
 *    (muncul jika >1 tahun; label via key awsc.allYears).
 * 7. Tabel detail bulanan (Monthly & Consolidated) saat >1 tahun terpilih: tambah
 *    label penjelas "X tahun digabung (range)" + baris kedua rata-rata/tahun di
 *    tiap sel angka, supaya total multi-tahun tidak disalahartikan sbg 1 bulan.
 * 8. Chart tab Consolidated diselaraskan dgn gaya Overview: tiap chart dlm panel
 *    sendiri (judul dot biru via S.cardTitle), tinggi tetap 300px +
 *    maintainAspectRatio:false → pie proporsional & tidak gepeng, legend di atas.
 * 9. Tab Usage Detail: tambah kotak search (filter Service / Usage Type, live,
 *    case-insensitive) + tombol clear ✕.
 * 10. Tab Products: tiap kolom kini bisa di-sort (klik header), mengikuti pola
 *     SortTh yg sudah ada di Usage Detail. Bar strip tetap pakai 10 teratas
 *     berdasarkan total (independen dari sort tabel).
 * 11. Istilah "Product" → "Service" (standar AWS) pada label tampilan kolom &
 *     dropdown di tab Products dan Usage Detail. Field data internal
 *     (Product Code / prod_month) TIDAK diubah agar koneksi API tetap utuh.
 * 12. Semua teks baru (note multi-tahun, suffix rata-rata, placeholder search,
 *     label "All", "Service") dipindah ke i18n via t(...). Lihat translations.js:
 *     key baru awsc.service/allYears/searchService/perYearSuffix/multiYearNote,
 *     serta perubahan awsc.product/productFilter/topProducts/tab.products.
 * 13. KPI cards di header diubah dari 5 BU (ID/JB/KL/No-Tags/Share) menjadi 3 BU
 *     teralokasi (ID/JB/KL) — Share & No-Tags dibagi rata ke 3 BU spt tab
 *     Consolidated. TotalBanner di atasnya tetap menampilkan 5 BU + total penuh.
 *     CSS grid .aws-kpi-row disesuaikan ke 3 kolom (1 kolom di mobile).
 * 14. Mode alokasi RASIO per akun (mis. akun Indomas): backend (awsCostAlloc.js) memecah
 *     TOTAL biaya ke company sesuai rasio berversi (effective_from). BUS/CONS_BUS/warna
 *     di-set ulang oleh applyAllocProfile() tiap data dimuat; KPI/Consolidated generik
 *     N kolom; tombol "Allocation" (admin) untuk edit rasio. Akun PIL tidak berubah.
 * ───────────────────────────────────────────────────────────────────────────
 */


import React, {
  useState, useEffect, useCallback, useMemo, useRef,
} from 'react';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, BarElement, LineElement,
  BarController, LineController,
  PointElement, ArcElement, Filler, Tooltip, Legend,
} from 'chart.js';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import { useSearchParams } from 'react-router-dom';
import useTabDeepLink from '../hooks/useTabDeepLink';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import SavingsPlanPanel from '../components/SavingsPlanPanel';

ChartJS.register(
  CategoryScale, LinearScale, BarElement, LineElement,
  BarController, LineController,
  PointElement, ArcElement, Filler, Tooltip, Legend,
);

// ── Constants ─────────────────────────────────────────────────────────────────
let BUS        = ['ID', 'JB', 'KL', 'No-Tags', 'Share'];
let CONS_BUS   = ['ID', 'JB', 'KL'];
let BU_COLORS  = { ID:'#1e7e4e', JB:'#b45309', KL:'#be185d', 'No-Tags':'#6d28d9', Share:'#0369a1' };
let CONS_COLORS= { ID:'#1e7e4e', JB:'#b45309', KL:'#be185d' };
// ── Profil alokasi per akun ──────────────────────────────────────────────────
// Akun PIL: BU dari tag (ID/JB/KL/No-Tags/Share); Share & No-Tags dibagi rata ke ID/JB/KL.
// Akun mode 'ratio' (mis. Indomas): backend sudah memecah TOTAL biaya ke company sesuai
// rasio (backend/awsCostAlloc.js). Nilai BUS/CONS_BUS/warna di-set ulang oleh
// applyAllocProfile() tiap data dimuat (SEBELUM setData), jadi semua tab ikut.
const TAG_PROFILE = { BUS:[...BUS], CONS_BUS:[...CONS_BUS], BU_COLORS:{...BU_COLORS}, CONS_COLORS:{...CONS_COLORS} };
const ALLOC_PALETTE = ['#1e7e4e','#b45309','#be185d','#0369a1','#6d28d9','#0f766e','#a16207','#4338ca','#15803d','#c2410c','#9333ea','#0e7490'];
let ALLOC = null;
function applyAllocProfile(alloc) {
  if (alloc && alloc.mode === 'ratio' && Array.isArray(alloc.units) && alloc.units.length) {
    ALLOC = alloc;
    BUS = alloc.units.map(u => u.code);
    CONS_BUS = [...BUS];
    BU_COLORS = Object.fromEntries(BUS.map((c, i) => [c, ALLOC_PALETTE[i % ALLOC_PALETTE.length]]));
    CONS_COLORS = { ...BU_COLORS };
  } else {
    ALLOC = null;
    BUS = [...TAG_PROFILE.BUS]; CONS_BUS = [...TAG_PROFILE.CONS_BUS];
    BU_COLORS = { ...TAG_PROFILE.BU_COLORS }; CONS_COLORS = { ...TAG_PROFILE.CONS_COLORS };
  }
}
const isRatioMode = () => !!ALLOC;
const allocUnit = code => (ALLOC && ALLOC.units.find(u => u.code === code)) || null;
const unitLabel = code => { const u = allocUnit(code); return (u && (u.short_name || u.name)) || code; };
const unitFullName = code => { const u = allocUnit(code); return (u && u.name) || ''; };
function allocRatioText() {
  if (!ALLOC) return '';
  const vs = ALLOC.versions || [];
  const cur = vs.length ? vs[vs.length - 1] : { units: ALLOC.units };
  return 'Total account cost split by fixed ratio: ' + cur.units.map(u => u.code + ' ' + u.pct + '%').join(' · ') +
    (vs.length > 1 ? ' (current ratio; earlier months use the ratio in effect at that time)' : '');
}
const MONTH_SHORT= ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const fmt  = v => '$' + Math.abs(v).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2});
const fmtK = v => Math.abs(v) >= 1000 ? '$'+(v/1000).toFixed(1)+'K' : '$'+v.toFixed(0);
// Format kuantitas pemakaian (tanpa simbol mata uang). Nilai ~0 → '-'.
// Angka besar pakai 2 desimal, angka kecil (pecahan GB-jam dsb.) sampai 4 desimal.
const fmtQty = v => {
  const n = Number(v);
  if (!isFinite(n) || Math.abs(n) < 1e-6) return '-';
  const dec = Math.abs(n) >= 1 ? 2 : 4;
  return n.toLocaleString('en-US',{ minimumFractionDigits:0, maximumFractionDigits:dec });
};
// Reserved Instance (HeavyUsage) tidak melaporkan UsageQuantity di Cost Explorer
// (jam-nya menempel pada baris BoxUsage ber-biaya $0). Untuk usage type seperti ini
// dgn qty 0 → tampilkan 'n/a' (bukan '-') agar tidak salah dibaca sbg "nol pemakaian".
const isQtyNA = (usageType, qty) =>
  /heavyusage/i.test(usageType || '') && Math.abs(Number(qty) || 0) < 1e-6;

// Jumlah hari dalam satu bulan (year, monthNum 1-12). Dipakai tab Daily Usage
// untuk menurunkan rata-rata harian dari total bulanan (data backend = MONTHLY).
const daysInMonth = (year, monthNum) => new Date(year, monthNum, 0).getDate();

// ── Usage Type decoder ────────────────────────────────────────────────────────
// Cost Explorer mengembalikan KODE usage type (mis. "APS1-EBS:VolumeUsage.gp3"),
// bukan deskripsi. Decoder ini mengubahnya jadi label yang lebih mudah dibaca:
// prefix region → nama region, sisanya → deskripsi keluarga layanan. Bila tidak
// dikenali, kode asli ditampilkan apa adanya (fallback aman).
const AWS_REGION_CODES = {
  USE1:'US East (N. Virginia)', USE2:'US East (Ohio)',
  USW1:'US West (N. California)', USW2:'US West (Oregon)',
  UGW1:'GovCloud (US-West)', UGE1:'GovCloud (US-East)',
  CAN1:'Canada (Central)', CAW1:'Canada West (Calgary)',
  SAE1:'South America (São Paulo)',
  EU:'EU (Ireland)', EUW1:'Europe (Ireland)', EUW2:'Europe (London)',
  EUW3:'Europe (Paris)', EUC1:'Europe (Frankfurt)', EUC2:'Europe (Zurich)',
  EUN1:'Europe (Stockholm)', EUS1:'Europe (Milan)', EUS2:'Europe (Spain)',
  APN1:'Asia Pacific (Tokyo)', APN2:'Asia Pacific (Seoul)', APN3:'Asia Pacific (Osaka)',
  APS1:'Asia Pacific (Singapore)', APS2:'Asia Pacific (Sydney)',
  APS3:'Asia Pacific (Mumbai)', APS4:'Asia Pacific (Jakarta)',
  APE1:'Asia Pacific (Hong Kong)', APS5:'Asia Pacific (Hyderabad)',
  MEC1:'Middle East (UAE)', MES1:'Middle East (Bahrain)',
  AFS1:'Africa (Cape Town)',
};
function describeUsageRest(r) {
  let m;
  if ((m = r.match(/^BoxUsage:?(.*)$/)))      return 'EC2 On-Demand' + (m[1] ? ' ' + m[1] : '') + ' (Instance Hours)';
  if ((m = r.match(/^SpotUsage:?(.*)$/)))      return 'EC2 Spot' + (m[1] ? ' ' + m[1] : '') + ' (Instance Hours)';
  if ((m = r.match(/^DedicatedUsage:?(.*)$/))) return 'EC2 Dedicated' + (m[1] ? ' ' + m[1] : '');
  if (/^HeavyUsage/.test(r))                   return 'EC2 Reserved Instance (Hours)';
  if ((m = r.match(/^EBS:VolumeUsage\.(\w+)$/))) return 'EBS ' + m[1] + ' Volume Storage';
  if (r === 'EBS:VolumeUsage')                 return 'EBS Magnetic Volume Storage';
  if (/^EBS:VolumeP-IOPS/.test(r))             return 'EBS Provisioned IOPS';
  if (/^EBS:VolumeIOUsage/.test(r))            return 'EBS I/O Requests';
  if (/^EBS:SnapshotArchiveStorage/.test(r))   return 'EBS Snapshot Archive Storage';
  if (/^EBS:Snapshot/.test(r))                 return 'EBS Snapshot Storage';
  if (/^EBS:/.test(r))                         return 'EBS ' + r.slice(4);
  if (/DataTransfer-Regional-Bytes/.test(r))   return 'Data Transfer — Regional (inter-AZ)';
  if (/DataTransfer-Out-Bytes/.test(r))        return 'Data Transfer Out (to Internet)';
  if (/DataTransfer-In-Bytes/.test(r))         return 'Data Transfer In (from Internet)';
  if (/AWS-Out-Bytes/.test(r))                 return 'Data Transfer Out (to other AWS Region)';
  if (/AWS-In-Bytes/.test(r))                  return 'Data Transfer In (from other AWS Region)';
  if (/CloudFront-Out-Bytes/.test(r))          return 'CloudFront Data Transfer Out';
  if (/^TimedStorage-INT/.test(r))             return 'S3 Intelligent-Tiering Storage';
  if (/^TimedStorage-GDA/.test(r))             return 'S3 Glacier Deep Archive Storage';
  if (/^TimedStorage-Glacier/.test(r) || /GlacierByteHrs/.test(r)) return 'S3 Glacier Storage';
  if (/^TimedStorage/.test(r))                 return 'S3 Standard Storage';
  if (/Requests-Tier1/.test(r))                return 'S3 PUT/COPY/POST/LIST Requests';
  if (/Requests-Tier2/.test(r))                return 'S3 GET & Other Requests';
  if (/Requests-Tier3/.test(r))                return 'Glacier Requests';
  if (/NatGateway-Hours/.test(r))              return 'NAT Gateway (Hours)';
  if (/NatGateway-Bytes/.test(r))              return 'NAT Gateway Data Processed';
  if (/LoadBalancerUsage/.test(r))             return 'Load Balancer (Hours)';
  if (/LCUUsage/.test(r))                      return 'Load Balancer Capacity Units (LCU)';
  if (/PublicIPv4/.test(r))                    return 'Public IPv4 Address (in-use)';
  if (/IdleAddress/.test(r))                   return 'Idle Elastic IP Address';
  if (/Lambda-GB-Second/.test(r))              return 'Lambda Compute (GB-seconds)';
  if (/^Requests?$/.test(r))                   return 'Requests';
  return r; // fallback: tampilkan kode apa adanya
}
function decodeUsageType(ut) {
  if (!ut) return { label: '', code: '' };
  if (ut === 'Tax') return { label: 'Tax', code: 'Tax' };
  let region = '', rest = ut;
  const idx = ut.indexOf('-');
  if (idx > 0) {
    const pre = ut.slice(0, idx);
    if (AWS_REGION_CODES[pre]) { region = AWS_REGION_CODES[pre]; rest = ut.slice(idx + 1); }
  }
  let label = describeUsageRest(rest);
  if (region) label += ' · ' + region;
  return { label, code: ut };
}
// Sel "Usage Type": label ramah + kode asli kecil di bawahnya.
function UsageTypeCell({ code, padLeft }) {
  const { label } = decodeUsageType(code);
  const showCode = label !== code;
  return (
    <td style={{ ...S.tdL, paddingLeft: padLeft }}>
      <span style={{ fontSize: 12, color: '#1a1a1a' }}>{label}</span>
      {showCode && (
        <span style={{ display:'block', fontSize:10, color:'#9ca3af',
          fontFamily:'"JetBrains Mono",monospace', marginTop:1 }}>{code}</span>
      )}
    </td>
  );
}

// Konteks multi-tahun: dipakai tabel detail bulanan agar tidak salah baca.
// Saat >1 tahun terpilih, angka per bulan = TOTAL semua tahun → kita sertakan
// label penjelas + rata-rata per tahun (total ÷ jumlah tahun).
// `t` opsional: bila diberikan, note & suffix mengikuti bahasa aktif.
function yearContext(selectedYears, t) {
  const yrs   = [...selectedYears].sort((a,b)=>a-b);
  const count = yrs.length;
  const multi = count > 1;
  const range = count === 0 ? '' : count === 1 ? String(yrs[0]) : `${yrs[0]}–${yrs[count-1]}`;
  const note = t
    ? t('awsc.multiYearNote').replace('{count}', count).replace('{range}', range)
    : `${count} tahun digabung (${range})`;
  const suffix = t ? t('awsc.perYearSuffix') : '/th';
  return { yrs, count, multi, range, note, suffix, avg: v => v / (count || 1) };
}

function getConsolidated(monthlyAll, selectedYears) {
  const rows = monthlyAll.filter(r => selectedYears.has(r.Year));
  const map = {};
  rows.forEach(r => {
    const m = r.MonthNum;
    if (!map[m]) map[m] = { monthNum:m, monthName:r.MonthName };
    map[m][r['Business Unit']] = (map[m][r['Business Unit']]||0) + r.USD;
  });
  return Object.values(map).sort((a,b)=>a.monthNum-b.monthNum).map(m=>{
    // Mode tag: Share & No-Tags dibagi rata ke BU teralokasi. Mode ratio: sudah per company.
    const shared = ALLOC ? 0 : Math.max(0,m.Share||0)+Math.max(0,m['No-Tags']||0);
    const alloc = shared/CONS_BUS.length;
    const out = { monthNum:m.monthNum, monthName:m.monthName, alloc };
    CONS_BUS.forEach(bu => { out[bu] = (m[bu]||0) + alloc; });
    return out;
  });
}

// ── API helpers ───────────────────────────────────────────────────────────────
const API = (window.__AWS_API_BASE__ || '') ; // set window.__AWS_API_BASE__ jika beda domain

// --- Multi-account AWS: akun aktif dibagi dengan halaman AWS Inventory ---
const COST_ACCT_LS = 'awsinv.account';
let COST_ACCT = 'primary';
try { COST_ACCT = localStorage.getItem(COST_ACCT_LS) || 'primary'; } catch (_) {}
const getCostAcct = () => COST_ACCT;
const switchCostAcct = (k) => {
  COST_ACCT = k;
  try { localStorage.setItem(COST_ACCT_LS, k); } catch (_) {}
  window.location.reload();
};

// --- "Refresh from AWS": re-fetch from Cost Explorer for the active account, with a 6-hour cooldown ---
// Cost Explorer charges USD 0.01 per API request and AWS refreshes cost data at least once
// every 24h, so re-fetching the same period within a few hours only pays for identical data.
// - Current month is always a candidate; the previous month only on day 1-5 (invoice finalisation).
// - A period fetched < 6h ago is skipped (no AWS request). If every period is fresh, the user can
//   still force a fetch after an explicit confirmation.
// - "Last fetched" comes from aws_uploads (AWS Cost: includes cron runs) and from this browser.
const AWS_REFRESH_COOLDOWN_MS = 6 * 60 * 60 * 1000;
function RefreshFromAwsButton({ endpoint, kind }) {
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState('');
  const [last, setLast] = React.useState({});
  const acct = getCostAcct();
  const token = localStorage.getItem('auth_token');
  const hdrs = { 'Content-Type': 'application/json', 'X-Aws-Account': acct, ...(token ? { Authorization: 'Bearer ' + token } : {}) };
  const label = (p) => p.year + '-' + String(p.month).padStart(2, '0');
  const lsKey = (p) => 'awsRefresh.' + kind + '.' + acct + '.' + label(p);
  const periods = React.useMemo(() => {
    const now = new Date();
    const cur = { year: now.getFullYear(), month: now.getMonth() + 1 };
    const prevD = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const prev = { year: prevD.getFullYear(), month: prevD.getMonth() + 1 };
    return now.getDate() <= 5 ? [prev, cur] : [cur];
  }, []);
  const loadLast = React.useCallback(async () => {
    const out = {};
    for (const p of periods) {
      let t = 0;
      try { t = Number(localStorage.getItem(lsKey(p))) || 0; } catch (_) {}
      out[label(p)] = t;
    }
    if (kind === 'cost') {
      try {
        const res = await fetch('/api/aws/uploads?account=' + encodeURIComponent(acct), { headers: hdrs });
        const d = await res.json();
        for (const u of (d.uploads || [])) {
          const m = /(\d{4})\/(\d{2}) \(fetch\)/.exec(u.filename || '');
          if (!m) continue;
          const k = m[1] + '-' + m[2];
          const t = Date.parse(String(u.uploaded_at).replace(' ', 'T') + 'Z');
          if (k in out && t > out[k]) out[k] = t;
        }
      } catch (_) {}
    }
    setLast(out);
    return out;
  }, [periods, acct, kind]);
  React.useEffect(() => { loadLast(); }, [loadLast]);
  const ago = (t) => {
    if (!t) return 'never';
    const m = Math.round((Date.now() - t) / 60000);
    const hhmm = new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return hhmm + ' (' + (m < 60 ? m + ' min' : Math.floor(m / 60) + 'h ' + (m % 60) + 'm') + ' ago)';
  };
  const run = async () => {
    const lastNow = await loadLast();
    const stale = periods.filter((p) => Date.now() - (lastNow[label(p)] || 0) >= AWS_REFRESH_COOLDOWN_MS);
    let todo = stale;
    if (!stale.length) {
      const newest = Math.max(...periods.map((p) => lastNow[label(p)] || 0));
      const ok = window.confirm(
        'Data for ' + periods.map(label).join(' & ') + ' was fetched ' + ago(newest) + '.\n' +
        'AWS refreshes cost data at least once every 24 hours, so a new fetch will most likely return the same numbers.\n\n' +
        'Fetch again anyway? This sends paid Cost Explorer requests (USD 0.01 each).'
      );
      if (!ok) { setMsg('Up to date — no AWS request sent.'); return; }
      todo = periods;
    } else if (!window.confirm('Refresh from AWS?\nFetches ' + stale.map(label).join(' and ') + ' from Cost Explorer for the selected AWS account and REPLACES those periods.' +
        (stale.length < periods.length ? '\n(Skipped, fetched < 6h ago: ' + periods.filter((p) => !stale.includes(p)).map(label).join(', ') + ')' : ''))) return;
    setBusy(true);
    const errs = [];
    let rows = 0;
    for (const p of todo) {
      setMsg('Fetching ' + label(p) + '…');
      try {
        const res = await fetch(endpoint + '?account=' + encodeURIComponent(acct), {
          method: 'POST', headers: hdrs,
          body: JSON.stringify({ year: p.year, month: p.month, mode: 'replace' }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || data.message || ('HTTP ' + res.status));
        rows += Number(data.count) || 0;
        try { localStorage.setItem(lsKey(p), String(Date.now())); } catch (_) {}
      } catch (e) {
        errs.push(label(p) + ': ' + e.message);
      }
    }
    setBusy(false);
    if (errs.length) { setMsg(''); window.alert('Refresh from AWS finished with errors:\n' + errs.join('\n')); if (errs.length === todo.length) return; }
    setMsg('Done — ' + rows + ' rows. Reloading…');
    setTimeout(() => window.location.reload(), 800);
  };
  const newest = Math.max(0, ...Object.values(last));
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:8 }}>
      <span style={{ fontSize:12, color:'rgba(255,255,255,0.75)' }}>{msg || ('Last fetched: ' + ago(newest))}</span>
      <button
        onClick={run}
        disabled={busy}
        title={'Fetch ' + periods.map(label).join(' & ') + ' from AWS Cost Explorer (skipped if fetched < 6h ago)'}
        style={{ padding:'6px 12px', borderRadius:8, border:'none', background: busy ? 'rgb(var(--brand-500))' : 'rgb(var(--brand-600))', color:'#fff', fontSize:13, fontWeight:600, cursor: busy ? 'wait' : 'pointer' }}
      >{busy ? '⟳ Fetching…' : '⟳ Refresh from AWS'}</button>
    </span>
  );
}


async function apiFetch(path, token, opts={}) {
  const res = await fetch(API + path, {
    ...opts,
    headers: { Authorization: `Bearer ${token}`, 'X-Aws-Account': getCostAcct(), ...(opts.headers||{}) },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

async function loadDashboardData(token, years=[]) {
  const params = years.length ? `?years=${years.join(',')}` : '';
  return apiFetch(`/api/aws/data${params}`, token);
}

// ── Styles (inline, tidak perlu CSS file) ─────────────────────────────────────
const S = {
  card: {
    background:'#fff', border:'1px solid #e2ddd8', borderRadius:12,
    padding:20, boxShadow:'0 1px 3px rgba(0,0,0,0.07)',
  },
  cardTitle: {
    fontSize:13, fontWeight:700, color:'#1a1a1a', marginBottom:16,
    display:'flex', alignItems:'center', gap:8,
  },
  dot: (color='rgb(var(--brand-600))') => ({
    width:8, height:8, borderRadius:'50%', background:color, display:'inline-block', flexShrink:0,
  }),
  select: {
    background:'#fff', border:'1px solid #d4cfc9', color:'#1a1a1a',
    borderRadius:8, padding:'5px 10px', fontSize:12, cursor:'pointer',
    fontFamily:'inherit',
  },
  pill: (active, isAll=false) => ({
    padding:'4px 12px', borderRadius:20, fontSize:11, fontWeight: active?700:500,
    border: active ? '1.5px solid rgb(var(--brand-600))' : '1px solid #d4cfc9',
    background: active ? (isAll ? 'rgb(var(--brand-600))' : 'rgb(var(--brand-50))') : '#fff',
    color: active ? (isAll ? '#fff' : 'rgb(var(--brand-600))') : '#6b7280',
    cursor:'pointer', fontFamily:'inherit', transition:'all .15s',
  }),
  th: { padding:'8px 12px', fontSize:11, fontWeight:600, color:'#6b7280',
        textTransform:'uppercase', letterSpacing:.8, borderBottom:'1px solid #e2ddd8',
        background:'#f0ede8', textAlign:'right', whiteSpace:'nowrap' },
  tdR: { padding:'8px 12px', textAlign:'right', fontSize:12,
         fontFamily:'"JetBrains Mono",monospace' },
  tdL: { padding:'8px 12px', textAlign:'left',  fontSize:12 },
};

// ── Tiny sub-components ───────────────────────────────────────────────────────

function Dot({ color }) { return <span style={S.dot(color)} />; }

function TableWrap({ children }) {
  return (
    <div style={{ overflowX:'auto' }}>
      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
        {children}
      </table>
    </div>
  );
}

function Th({ children, left, style={} }) {
  return <th style={{ ...S.th, textAlign: left?'left':'right', ...style }}>{children}</th>;
}

function Td({ children, color, bold, left }) {
  return (
    <td style={{ ...S.tdR, ...(left ? {textAlign:'left', fontFamily:'inherit'} : {}),
      color: color||'inherit', fontWeight: bold?700:400 }}>
      {children}
    </td>
  );
}

function BreakdownSub({ title, rows, kind, L, pctFmt, pctColor }) {
  const mono = { fontFamily: '"JetBrains Mono",monospace' };
  const cell = { padding: '4px 8px', textAlign: 'right' };
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 6, textTransform: 'uppercase', letterSpacing: .3 }}>{title}</div>
      {rows.length ? (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11.5 }}>
          <thead><tr>
            <th style={{ ...S.th, textAlign: 'left', padding: '4px 8px' }}>{kind === 'bu' ? 'BU' : 'Usage type'}</th>
            <th style={{ ...S.th, ...cell }}>{L('Sebelum', 'Prev')}</th>
            <th style={{ ...S.th, ...cell }}>{L('Sekarang', 'Current')}</th>
            <th style={{ ...S.th, ...cell }}>Δ USD</th>
            <th style={{ ...S.th, ...cell }}>Δ %</th>
          </tr></thead>
          <tbody>
            {rows.slice(0, 12).map((e, i) => (
              <tr key={i} style={{ borderTop: '1px solid #f0ede8' }}>
                <td style={{ padding: '4px 8px', textAlign: 'left' }}>
                  {kind === 'bu'
                    ? <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: BU_COLORS[e.name] || '#9ca3af', marginRight: 6 }} />{e.name}</span>
                    : decodeUsageType(e.name).label}
                </td>
                <td style={{ ...cell, ...mono }}>{fmt(e.prev)}</td>
                <td style={{ ...cell, ...mono }}>{fmt(e.cur)}</td>
                <td style={{ ...cell, fontWeight: 700, color: e.delta > 0 ? '#dc2626' : '#15803d' }}>{(e.delta > 0 ? '+' : '−') + fmt(e.delta)}</td>
                <td style={{ ...cell, color: pctColor(e.pct) }}>{pctFmt(e.pct)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : <div style={{ fontSize: 11, color: '#9ca3af' }}>{L('Tidak ada perubahan.', 'No changes.')}</div>}
    </div>
  );
}

function NoData() {
  const { t } = useLang();
  return <tr><td colSpan={99} style={{ textAlign:'center', color:'#9ca3af', padding:24, fontSize:12 }}>{t('awsc.noData')}</td></tr>;
}

// ── Allocation settings (akun mode ratio) ─────────────────────────────────────
// Edit rasio company per akun AWS. Berversi per bulan efektif (YYYY-MM). Hanya admin
// yang bisa menyimpan (backend juga menolak non-admin). Setelah ada perubahan, halaman
// di-reload saat modal ditutup agar semua tab memakai rasio baru.
function AllocSettingsButton({ token }) {
  const [open, setOpen] = useState(false);
  return (
    <React.Fragment>
      <button onClick={()=>setOpen(true)} title="Company cost allocation ratio for this AWS account"
        style={{ padding:'6px 12px', borderRadius:8, border:'1px solid #e2ddd8', background:'#fff',
          color:'#374151', fontSize:13, fontWeight:600, cursor:'pointer' }}>⚖ Allocation</button>
      {open && <AllocSettingsModal token={token} onClose={()=>setOpen(false)} />}
    </React.Fragment>
  );
}

function AllocSettingsModal({ token, onClose }) {
  const acct = getCostAcct();
  let isAdmin = false;
  try { isAdmin = JSON.parse(localStorage.getItem('auth_user') || '{}').role === 'admin'; } catch (_) {}
  const [versions, setVersions] = useState((ALLOC && ALLOC.versions) || []);
  const [sel, setSel] = useState(null); // effective_from yg diedit, '__new' = versi baru
  const [eff, setEff] = useState('');
  const [units, setUnits] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [dirty, setDirty] = useState(false);
  const hdrs = { 'Content-Type':'application/json', Authorization:'Bearer ' + token, 'X-Aws-Account': acct };
  const base = '/api/aws/alloc?account=' + encodeURIComponent(acct);

  useEffect(() => {
    fetch(base, { headers: hdrs }).then(r => r.json())
      .then(d => { if (Array.isArray(d.versions)) setVersions(d.versions); }).catch(() => {});
  }, []); // eslint-disable-line

  const edit = (v) => { setSel(v.effective_from); setEff(v.effective_from); setUnits(v.units.map(u => ({ ...u }))); setErr(''); };
  const addVersion = () => {
    const last = versions[versions.length - 1];
    const d = new Date();
    setSel('__new'); setEff(d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0'));
    setUnits(last ? last.units.map(u => ({ ...u })) : [{ code:'', short_name:'', name:'', pct:100 }]); setErr('');
  };
  const setU = (i, k, v) => setUnits(us => us.map((u, j) => j === i ? { ...u, [k]: v } : u));
  const sum = units.reduce((s, u) => s + (Number(u.pct) || 0), 0);
  const sumOk = Math.abs(sum - 100) < 0.001;

  const call = async (method, url, body) => {
    const r = await fetch(url, { method, headers: hdrs, body: body ? JSON.stringify(body) : undefined });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d.error || ('HTTP ' + r.status));
    return d;
  };
  const save = async () => {
    setBusy(true); setErr('');
    try {
      let d = await call('PUT', base, { effective_from: eff, units: units.map(u => ({ ...u, pct: Number(u.pct) })) });
      // Bulan efektif versi lama diganti → hapus versi lama.
      if (sel && sel !== '__new' && sel !== eff) d = await call('DELETE', base + '&effective_from=' + encodeURIComponent(sel));
      setVersions(d.versions || []); setSel(null); setDirty(true);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const remove = async (v) => {
    if (!window.confirm('Delete the ratio effective from ' + v.effective_from + '?')) return;
    setBusy(true); setErr('');
    try {
      const d = await call('DELETE', base + '&effective_from=' + encodeURIComponent(v.effective_from));
      setVersions(d.versions || []); setSel(null); setDirty(true);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const close = () => { onClose(); if (dirty) window.location.reload(); };
  const inp = { padding:'5px 8px', border:'1px solid #d4cfc9', borderRadius:6, fontSize:12,
    fontFamily:'inherit', width:'100%', boxSizing:'border-box' };
  const btn = (primary) => ({ padding:'6px 12px', borderRadius:8, fontSize:12, fontWeight:600, cursor:'pointer',
    border: primary ? 'none' : '1px solid #d4cfc9', background: primary ? 'rgb(var(--brand-600))' : '#fff',
    color: primary ? '#fff' : '#374151' });
  const link = { background:'none', border:'none', color:'rgb(var(--brand-700))', cursor:'pointer', fontSize:12, fontWeight:600, padding:'0 4px' };

  return (
    <div onClick={close} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.35)', zIndex:9998,
      display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
      <div onClick={e => e.stopPropagation()} style={{ ...S.card, width:'min(760px,100%)', maxHeight:'90vh', overflow:'auto' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
          <div style={{ fontSize:15, fontWeight:700, color:'#1a1a1a' }}>⚖ Company cost allocation</div>
          <button onClick={close} style={{ ...link, fontSize:18, color:'#6b7280' }}>✕</button>
        </div>
        <div style={{ fontSize:12, color:'#6b7280', marginBottom:14, lineHeight:1.5 }}>
          The total cost of this AWS account is split across companies using the ratios below. Each month uses the
          latest ratio whose effective month is on or before it; months before the first ratio use the first one.
          Rounding differences (cents) go to the company with the largest share.
        </div>

        <TableWrap>
          <thead><tr><Th left>Effective from</Th><Th left>Split</Th>{isAdmin && <Th>Actions</Th>}</tr></thead>
          <tbody>
            {versions.length === 0 ? <NoData/> : versions.map(v => (
              <tr key={v.effective_from} style={{ borderBottom:'1px solid #f0ede8', background: sel === v.effective_from ? '#f8f6f3' : undefined }}>
                <Td left>{v.effective_from}</Td>
                <Td left>{v.units.map(u => u.code + ' ' + u.pct + '%').join(' · ')}</Td>
                {isAdmin && <td style={{ ...S.tdR, whiteSpace:'nowrap' }}>
                  <button style={link} disabled={busy} onClick={() => edit(v)}>Edit</button>
                  {versions.length > 1 && <button style={{ ...link, color:'#dc2626' }} disabled={busy} onClick={() => remove(v)}>Delete</button>}
                </td>}
              </tr>
            ))}
          </tbody>
        </TableWrap>

        {isAdmin && !sel && (
          <div style={{ marginTop:12 }}>
            <button style={btn(false)} onClick={addVersion}>+ New ratio (from a month onward)</button>
          </div>
        )}

        {isAdmin && sel && (
          <div style={{ marginTop:16, padding:14, border:'1px solid #e2ddd8', borderRadius:10, background:'#fcfbf9' }}>
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12, flexWrap:'wrap' }}>
              <span style={{ fontSize:12, fontWeight:700, color:'#374151' }}>{sel === '__new' ? 'New ratio' : 'Edit ratio'} · effective from</span>
              <input type="month" value={eff} onChange={e => setEff(e.target.value)} style={{ ...inp, width:160 }} />
            </div>
            <TableWrap>
              <thead><tr><Th left>Code</Th><Th left>Short name</Th><Th left>Company name</Th><Th>%</Th><Th> </Th></tr></thead>
              <tbody>
                {units.map((u, i) => (
                  <tr key={i}>
                    <td style={{ padding:4, width:90 }}><input style={inp} value={u.code} maxLength={10} onChange={e => setU(i, 'code', e.target.value.toUpperCase())} /></td>
                    <td style={{ padding:4, width:120 }}><input style={inp} value={u.short_name || ''} onChange={e => setU(i, 'short_name', e.target.value)} /></td>
                    <td style={{ padding:4 }}><input style={inp} value={u.name || ''} onChange={e => setU(i, 'name', e.target.value)} /></td>
                    <td style={{ padding:4, width:80 }}><input style={{ ...inp, textAlign:'right' }} type="number" min="0" max="100" step="0.01" value={u.pct} onChange={e => setU(i, 'pct', e.target.value)} /></td>
                    <td style={{ padding:4, width:30, textAlign:'center' }}>
                      {units.length > 1 && <button style={{ ...link, color:'#dc2626' }} onClick={() => setUnits(us => us.filter((_, j) => j !== i))}>✕</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:10, flexWrap:'wrap', gap:10 }}>
              <button style={link} onClick={() => setUnits(us => [...us, { code:'', short_name:'', name:'', pct:0 }])}>+ Add company</button>
              <span style={{ fontSize:12, fontWeight:700, color: sumOk ? '#15803d' : '#dc2626' }}>
                Total: {Math.round(sum * 100) / 100}%{sumOk ? '' : ' (must be 100%)'}
              </span>
            </div>
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:12 }}>
              <button style={btn(false)} disabled={busy} onClick={() => { setSel(null); setErr(''); }}>Cancel</button>
              <button style={{ ...btn(true), opacity: (!sumOk || busy) ? .5 : 1 }} disabled={!sumOk || busy} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        )}

        {!isAdmin && <div style={{ fontSize:11, color:'#9ca3af', marginTop:10 }}>Only admins can change the allocation ratio.</div>}
        {err && <div style={{ marginTop:10, color:'#dc2626', fontSize:12 }}>⚠ {err}</div>}
        {dirty && !sel && <div style={{ marginTop:10, color:'#15803d', fontSize:12 }}>Saved. The dashboard will reload when you close this dialog.</div>}
      </div>
    </div>
  );
}

// ── Toast ─────────────────────────────────────────────────────────────────────
function Toast({ msg, type, show }) {
  const bg = type==='error'?'#dc2626':type==='success'?'#15803d':'#1a1a1a';
  return (
    <div style={{
      position:'fixed', bottom:24, left:'50%',
      transform: `translateX(-50%) translateY(${show?'0':'80px'})`,
      background:bg, color:'#fff', padding:'10px 22px', borderRadius:8,
      fontSize:13, zIndex:9999, opacity: show?1:0,
      transition:'transform .3s, opacity .3s', pointerEvents:'none', whiteSpace:'nowrap',
    }}>{msg}</div>
  );
}

// ── Year selector (Header strip) ──────────────────────────────────────────────
// ── KPI Cards ─────────────────────────────────────────────────────────────────
function KpiCards({ yearlyBU, selectedYears }) {
  const { t } = useLang();
  // Mode tag (PIL): sama seperti tab Consolidated, Share & No-Tags dibagi rata ke BU
  // teralokasi. Mode ratio (mis. Indomas): data sudah dipecah per company di backend.
  const { agg, total } = useMemo(()=>{
    const rows = yearlyBU.filter(r=>selectedYears.has(r.Year));
    const sum = bu => rows.reduce((s,r)=>s+(r[bu]||0),0);
    const alloc = ALLOC ? 0 : (Math.max(0,sum('Share')) + Math.max(0,sum('No-Tags'))) / CONS_BUS.length;
    const agg = Object.fromEntries(CONS_BUS.map(bu=>[bu, sum(bu) + alloc]));
    const total = CONS_BUS.reduce((s,bu)=>s+(agg[bu]||0),0);
    return { agg, total };
  }, [yearlyBU, selectedYears]);

  return (
    <div style={{ display:'grid', gridTemplateColumns:`repeat(${CONS_BUS.length},1fr)`, gap:12, marginBottom:20 }}
      className="aws-kpi-row">
      {CONS_BUS.map(bu=>{
        const v = agg[bu]||0, pct = total>0?((v/total)*100).toFixed(0):0;
        const c = CONS_COLORS[bu];
        const full = ALLOC ? unitFullName(bu) : '';
        const short = ALLOC ? unitLabel(bu) : bu;
        return (
          <div key={bu} className="aws-kpi-card" title={full || undefined}
            style={{ ...S.card, borderTop:`3px solid ${c}`, padding:'16px 18px' }}>
            <div style={{ fontSize:10, fontWeight:700, textTransform:'uppercase',
              letterSpacing:1, color:'#6b7280', marginBottom: full ? 2 : 6 }}>
              {bu}{short !== bu && <span style={{ textTransform:'none', letterSpacing:0, color:'#9ca3af' }}> · {short}</span>}
            </div>
            {full && <div className="aws-kpi-oftotal" style={{ fontSize:10, color:'#9ca3af', marginBottom:6,
              whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{full}</div>}
            <div className="aws-kpi-val" style={{ fontFamily:'"JetBrains Mono",monospace', fontSize:20,
              fontWeight:700, color:'#1a1a1a', marginBottom:4 }}>{fmtK(v)}</div>
            <div style={{ fontSize:11, color:'#6b7280', display:'flex', alignItems:'center', gap:5 }}>
              <span style={{ background:c+'1a', color:c, borderRadius:20,
                padding:'2px 7px', fontSize:11, fontWeight:700 }}>{pct}%</span>
              <span className="aws-kpi-oftotal">{t('awsc.ofTotal')}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Total Banner ──────────────────────────────────────────────────────────────
function TotalBanner({ yearlyBU, selectedYears }) {
  const { t } = useLang();
  const agg = useMemo(()=>{
    const rows = yearlyBU.filter(r=>selectedYears.has(r.Year));
    return Object.fromEntries(BUS.map(bu=>[bu, rows.reduce((s,r)=>s+(r[bu]||0),0)]));
  },[yearlyBU,selectedYears]);
  const total = BUS.reduce((s,bu)=>s+(agg[bu]||0),0);
  const yrs   = [...selectedYears].sort((a,b)=>a-b);
  const label = yrs.length===0?t('awsc.noData'): yrs.join(', ');

  return (
    <div style={{ ...S.card, display:'flex', justifyContent:'space-between',
                  alignItems:'center', marginBottom:20, flexWrap:'wrap', gap:16,
                  padding:'18px 24px' }}>
      <div>
        <div style={{ fontSize:11, fontWeight:700, textTransform:'uppercase',
                      letterSpacing:1, color:'#6b7280' }}>AWS Cost · {label}</div>
        <div style={{ fontFamily:'"JetBrains Mono",monospace', fontSize:30,
                      fontWeight:800, color:'#1a1a1a', margin:'4px 0 2px' }}>{fmt(total)}</div>
        <div style={{ fontSize:11, color:'#9ca3af' }}>{ALLOC ? ('Account total · split across ' + CONS_BUS.length + ' companies by fixed ratio') : t('awsc.allBuCombined')}</div>
      </div>
      <div style={{ display:'flex', gap:16, flexWrap:'wrap' }}>
        {BUS.map(bu=>(
          <div key={bu} style={{ textAlign:'center' }}>
            <div style={{ fontSize:10, fontWeight:700, textTransform:'uppercase',
                          letterSpacing:.8, color:'#9ca3af' }}>{bu}</div>
            <div style={{ fontFamily:'"JetBrains Mono",monospace', fontSize:15,
                          fontWeight:700, color:BU_COLORS[bu] }}>{fmtK(agg[bu]||0)}</div>
            <div style={{ fontSize:10, color:'#9ca3af', fontFamily:'"JetBrains Mono",monospace' }}>
              {total>0?((agg[bu]||0)/total*100).toFixed(1):0}%
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Charts (Overview tab) ─────────────────────────────────────────────────────
const CHART_OPTS_BASE = {
  responsive:true, maintainAspectRatio:true,
  plugins:{ legend:{ labels:{ color:'#374151', font:{size:11} } } },
};

function MonthlyChart({ monthlyAll, selectedYears }) {
  const { t } = useLang();
  const { labels, datasets } = useMemo(()=>{
    const yrs = [...selectedYears].sort((a,b)=>a-b);
    const rows = monthlyAll.filter(r=>selectedYears.has(r.Year));
    const entries=[];
    yrs.forEach(yr=>{
      const months=[...new Set(rows.filter(r=>r.Year===yr).map(r=>r.MonthNum))].sort((a,b)=>a-b);
      months.forEach(m=>{
        const mn=(rows.find(r=>r.Year===yr&&r.MonthNum===m)||{}).MonthName||m;
        entries.push({ yr, m, label: yrs.length===1?mn:`${mn} ${yr}` });
      });
    });
    return {
      labels: entries.map(e=>e.label),
      datasets: BUS.map(bu=>({
        label:bu,
        data: entries.map(({yr,m})=>{ const r=rows.find(x=>x.Year===yr&&x.MonthNum===m&&x['Business Unit']===bu); return r?Math.max(0,r.USD):0; }),
        backgroundColor: BU_COLORS[bu]+'cc', borderColor:BU_COLORS[bu],
        borderWidth:1, borderRadius:3,
      })),
    };
  },[monthlyAll,selectedYears]);

  return (
    <div style={S.card}>
      <div style={S.cardTitle}><Dot color="rgb(var(--brand-600))"/>{t('awsc.monthlyCostByBu')}</div>
      <div style={{ height:260, position:'relative' }}>
        <Bar data={{ labels, datasets }} options={{ ...CHART_OPTS_BASE,
          maintainAspectRatio:false,
          scales:{ x:{stacked:true,grid:{color:'#e5e7eb'},ticks:{color:'#6b7280',font:{size:11}}},
                   y:{stacked:true,grid:{color:'#e5e7eb'},ticks:{color:'#6b7280',callback:v=>fmtK(v),font:{size:11}}} } }} />
      </div>
    </div>
  );
}

// ── Donat + legend berangka + total di tengah ────────────────────────────────
// Legend bawaan Chart.js diganti legend HTML agar bisa memuat nominal & persen.
// Total di tengah dipasang sebagai overlay absolut (Chart.js tak punya slot teks
// tengah bawaan untuk doughnut).
function DonutWithTotal({ labels, values, colors, height = 300, totalLabel = 'Total' }) {
  const grand = values.reduce((s, v) => s + v, 0);
  return (
    <div>
      <div style={{ display:'flex', flexWrap:'wrap', gap:'8px 14px', marginBottom:12, justifyContent:'center' }}>
        {labels.map((lab, i) => (
          <span key={lab} style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:'#374151' }}>
            <span style={{ width:11, height:11, borderRadius:3, background:colors[i], flexShrink:0 }} />
            <span style={{ fontWeight:600 }}>{lab}</span>
            <span style={{ color:'#4b5563' }}>{fmtK(values[i])}</span>
            <span style={{ color:'#9ca3af' }}>
              ({grand > 0 ? (values[i] / grand * 100).toFixed(1) : '0.0'}%)
            </span>
          </span>
        ))}
      </div>
      <div style={{ height, position:'relative' }}>
        <Doughnut
          data={{ labels, datasets:[{ data:values, backgroundColor:colors,
            borderColor:'#fff', borderWidth:3, hoverOffset:8 }] }}
          options={{ ...CHART_OPTS_BASE, maintainAspectRatio:false, cutout:'68%',
            plugins:{ legend:{ display:false },
              tooltip:{ callbacks:{ label:ctx => {
                const pct = grand > 0 ? (ctx.raw / grand * 100).toFixed(1) : '0.0';
                return ` ${ctx.label}: ${fmt(ctx.raw)} (${pct}%)`;
              } } } } }} />
        <div style={{ position:'absolute', top:'50%', left:'50%', transform:'translate(-50%,-50%)',
                      textAlign:'center', pointerEvents:'none' }}>
          <div style={{ fontSize:10, fontWeight:700, color:'#9ca3af',
                        textTransform:'uppercase', letterSpacing:'.06em' }}>
            {totalLabel}
          </div>
          <div style={{ fontSize:20, fontWeight:800, color:'#1a1a1a', marginTop:2 }}>
            {fmt(grand)}
          </div>
        </div>
      </div>
    </div>
  );
}

function PieChart({ yearlyBU, selectedYears }) {
  const { t } = useLang();
  const { values } = useMemo(()=>{
    const rows = yearlyBU.filter(r=>selectedYears.has(r.Year));
    const agg  = Object.fromEntries(BUS.map(bu=>[bu, rows.reduce((s,r)=>s+(r[bu]||0),0)]));
    return { values: BUS.map(bu=>Math.max(0,agg[bu]||0)) };
  },[yearlyBU,selectedYears]);

  return (
    <div style={S.card}>
      <div style={S.cardTitle}><Dot color="rgb(var(--brand-600))"/>{t('awsc.costShareByBu')}</div>
      <DonutWithTotal labels={BUS} values={values}
        colors={BUS.map(bu=>BU_COLORS[bu])} height={290} totalLabel={t('awsc.total')} />
    </div>
  );
}

function YearlyChart({ yearlyBU }) {
  const { t } = useLang();
  const { labels, datasets } = useMemo(()=>{
    const map={};
    yearlyBU.forEach(r=>{ if(!map[r.Year]) map[r.Year]={Year:r.Year};
      BUS.forEach(bu=>{ map[r.Year][bu]=(map[r.Year][bu]||0)+(r[bu]||0); }); });
    const sorted=Object.values(map).sort((a,b)=>a.Year-b.Year);
    return { labels:sorted.map(d=>d.Year),
      datasets: BUS.map(bu=>({ label:bu,
        data:sorted.map(d=>d[bu]||0),
        borderColor:BU_COLORS[bu], backgroundColor:BU_COLORS[bu]+'18',
        fill:true, tension:.3, pointRadius:4, pointHoverRadius:7 })) };
  },[yearlyBU]);

  return (
    <div style={{ ...S.card, gridColumn:'1/-1' }}>
      <div style={S.cardTitle}><Dot color="rgb(var(--brand-600))"/>{t('awsc.yearlyTrend')}</div>
      <div style={{ height:260 }}>
        <Line data={{ labels, datasets }}
          options={{ ...CHART_OPTS_BASE, maintainAspectRatio:false,
            scales:{ x:{grid:{color:'#e5e7eb'},ticks:{color:'#6b7280'}},
              y:{grid:{color:'#e5e7eb'},ticks:{color:'#6b7280',callback:v=>fmtK(v)}} } }} />
      </div>
    </div>
  );
}

// ── Consolidated Tab ──────────────────────────────────────────────────────────
function ConsolidatedTab({ monthlyAll, selectedYears }) {
  const { t } = useLang();
  const [selMon,  setSelMon]  = useState('all');
  const [viewMode,setViewMode]= useState('bar');

  const monthly = useMemo(()=>getConsolidated(monthlyAll,selectedYears),[monthlyAll,selectedYears]);
  const months  = useMemo(()=>monthly.map(m=>({num:m.monthNum,name:m.monthName})),[monthly]);

  const filtered = selMon==='all' ? monthly : monthly.filter(m=>m.monthNum===selMon);
  const totals = Object.fromEntries(CONS_BUS.map(bu=>[bu,0]));
  filtered.forEach(m=>CONS_BUS.forEach(bu=>{ totals[bu]+=m[bu]; }));
  const grand = CONS_BUS.reduce((s,bu)=>s+totals[bu],0);

  const isSingle = filtered.length===1;
  const yc = yearContext(selectedYears, t);
  const chartLabels = isSingle?['Total']:filtered.map(m=>m.monthName);
  const chartData = {
    labels: chartLabels,
    datasets: CONS_BUS.map(bu=>({
      label:bu,
      data: isSingle?[totals[bu]]:filtered.map(m=>m[bu]),
      backgroundColor: CONS_COLORS[bu]+(viewMode==='bar'?'cc':'22'),
      borderColor: CONS_COLORS[bu],
      borderWidth: viewMode==='line'?2.5:1,
      borderRadius: viewMode==='bar'?4:0,
      fill: viewMode==='line', tension:.35,
      pointRadius: viewMode==='line'?4:0,
    })),
  };
  const scalesBoth = { x:{ stacked:true, grid:{color:'#e5e7eb'}, ticks:{color:'#6b7280'} },
    y:{ stacked:true, grid:{color:'#e5e7eb'}, ticks:{color:'#6b7280',callback:v=>fmtK(v)} } };
  const scalesLine = { x:{ grid:{color:'#e5e7eb'}, ticks:{color:'#6b7280'} },
    y:{ grid:{color:'#e5e7eb'}, ticks:{color:'#6b7280',callback:v=>fmtK(v)} } };

  return (
    <div style={S.card}>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', flexWrap:'wrap',
                    gap:12, marginBottom:14, alignItems:'flex-start' }}>
        <div>
          <div style={{ fontSize:13, fontWeight:700, color:'#1a1a1a', display:'flex', gap:8, alignItems:'center' }}>
            <Dot color="rgb(var(--brand-600))"/>{t('awsc.tab.consolidated')} — {CONS_BUS.join(' / ')}
            <span style={{ fontSize:10, background:'rgb(var(--brand-50))', color:'rgb(var(--brand-600))', border:'1px solid rgb(var(--brand-200))',
              padding:'2px 8px', borderRadius:20, fontWeight:700, letterSpacing:.5 }}>{ALLOC ? 'Fixed ratio' : t('awsc.allocated')}</span>
          </div>
          <div style={{ fontSize:11, color:'#9ca3af', fontStyle:'italic', marginTop:3 }}>
            {ALLOC ? allocRatioText() : t('awsc.shareNoTags')}
          </div>
        </div>
        <select style={S.select} value={viewMode} onChange={e=>setViewMode(e.target.value)}>
          <option value="bar">{t('awsc.barChart')}</option>
          <option value="line">{t('awsc.lineChart')}</option>
        </select>
      </div>

      {/* Month pills */}
      <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:18 }}>
        <button style={S.pill(selMon==='all',true)} onClick={()=>setSelMon('all')}>{t('awsc.allMonths')}</button>
        {months.map(m=>(
          <button key={m.num} style={S.pill(selMon===m.num)} onClick={()=>setSelMon(m.num)}>{m.name}</button>
        ))}
      </div>

      {/* KPI mini cards */}
      <div style={{ display:'grid', gridTemplateColumns:`repeat(${CONS_BUS.length},1fr)`, gap:10, marginBottom:18 }}
           className="aws-cons-kpi">
        {CONS_BUS.map(bu=>{
          const pct=grand>0?(totals[bu]/grand*100).toFixed(1):0;
          return (
            <div key={bu} className="aws-cons-card" style={{ ...S.card, borderLeft:`3px solid ${CONS_COLORS[bu]}`,
              display:'flex', justifyContent:'space-between', alignItems:'center',
              background:'#f8f6f3', padding:'12px 16px' }}>
              <div>
                <div style={{ fontSize:10, fontWeight:800, textTransform:'uppercase',
                  letterSpacing:1, color:CONS_COLORS[bu], marginBottom:4 }}>{bu}</div>
                <div className="aws-cons-val" style={{ fontFamily:'"JetBrains Mono",monospace', fontSize:18,
                  fontWeight:700, color:'#1a1a1a' }}>{fmt(totals[bu])}</div>
                <div className="aws-cons-alloc" style={{ fontSize:10, color:'#9ca3af', marginTop:2 }}>{ALLOC ? (unitFullName(bu) || unitLabel(bu)) : t('awsc.inclAlloc')}</div>
              </div>
              <div className="aws-cons-pct" style={{ fontFamily:'"JetBrains Mono",monospace', fontSize:22,
                fontWeight:800, color:CONS_COLORS[bu] }}>{pct}%</div>
            </div>
          );
        })}
      </div>

      {/* Charts */}
      <div style={{ display:'grid', gridTemplateColumns:'1.4fr 1fr', gap:18, marginBottom:20 }}
           className="aws-cons-charts">
        <div style={{ background:'#f8f6f3', border:'1px solid #e2ddd8', borderRadius:10, padding:16 }}>
          <div style={S.cardTitle}><Dot color="rgb(var(--brand-600))"/>{t('awsc.costPerBu')}</div>
          <div style={{ height:300, position:'relative' }}>
            {viewMode==='bar'
              ? <Bar  data={chartData} options={{ ...CHART_OPTS_BASE, maintainAspectRatio:false, scales:scalesBoth }} />
              : <Line data={chartData} options={{ ...CHART_OPTS_BASE, maintainAspectRatio:false, scales:scalesLine }} />}
          </div>
        </div>
        <div style={{ background:'#f8f6f3', border:'1px solid #e2ddd8', borderRadius:10, padding:16 }}>
          <div style={S.cardTitle}><Dot color="rgb(var(--brand-600))"/>{t('awsc.proportion')}</div>
          <DonutWithTotal labels={CONS_BUS} values={CONS_BUS.map(bu=>Math.max(0,totals[bu]))}
            colors={CONS_BUS.map(bu=>CONS_COLORS[bu])} height={270} totalLabel={t('awsc.total')} />
        </div>
      </div>

      {/* Monthly breakdown */}
      <div style={{ fontSize:12, fontWeight:700, color:'#1a1a1a', marginBottom: yc.multi?2:10 }}>
        {t('awsc.detailPerMonth')}
      </div>
      {yc.multi && (
        <div style={{ fontSize:11, color:'#9ca3af', fontStyle:'italic', marginBottom:10 }}>
          {yc.note}
        </div>
      )}
      <TableWrap>
        <thead><tr>
          <Th left>{t('awsc.month')}</Th>
          {CONS_BUS.map(bu=><Th key={bu}>{bu}</Th>)}
          <Th>{t('awsc.total')}</Th>
        </tr></thead>
        <tbody>
          {filtered.length===0 ? <NoData/> : filtered.map(m=>{
            const rt=CONS_BUS.reduce((s,bu)=>s+m[bu],0);
            return (<tr key={m.monthNum} style={{ borderBottom:'1px solid #f0ede8' }}>
              <Td left>{m.monthName}</Td>
              {CONS_BUS.map(bu=>(
                <td key={bu} style={{ ...S.tdR, color:CONS_COLORS[bu] }}>
                  {fmt(m[bu])}
                  {yc.multi && <div style={{ fontSize:10, color:'#9ca3af' }}>{fmt(yc.avg(m[bu]))}{yc.suffix}</div>}
                </td>
              ))}
              <td style={{ ...S.tdR, fontWeight:700 }}>
                {fmt(rt)}
                {yc.multi && <div style={{ fontSize:10, color:'#9ca3af', fontWeight:400 }}>{fmt(yc.avg(rt))}{yc.suffix}</div>}
              </td>
            </tr>);
          })}
        </tbody>
        <tfoot><tr style={{ background:'#f0ede8' }}>
          <td style={{ ...S.tdL, fontWeight:700, borderTop:'2px solid #d4cfc9' }}>{t('awsc.grandTotal')}</td>
          {CONS_BUS.map(bu=>(
            <td key={bu} style={{ ...S.tdR, fontWeight:700, borderTop:'2px solid #d4cfc9' }}>
              {fmt(totals[bu])}
              {yc.multi && <div style={{ fontSize:10, color:'#9ca3af', fontWeight:400 }}>{fmt(yc.avg(totals[bu]))}{yc.suffix}</div>}
            </td>
          ))}
          <td style={{ ...S.tdR, fontWeight:700, borderTop:'2px solid #d4cfc9' }}>
            {fmt(grand)}
            {yc.multi && <div style={{ fontSize:10, color:'#9ca3af', fontWeight:400 }}>{fmt(yc.avg(grand))}{yc.suffix}</div>}
          </td>
        </tr></tfoot>
      </TableWrap>
    </div>
  );
}

// ── Monthly Tab ───────────────────────────────────────────────────────────────
function MonthlyTab({ monthlyAll, selectedYears }) {
  const { t } = useLang();
  const [buFilter, setBuFilter] = useState('all');

  const { rows, totals, grand } = useMemo(()=>{
    const data = monthlyAll.filter(r=>selectedYears.has(r.Year));
    const map  = {};
    data.forEach(r=>{
      if(!map[r.MonthNum]) map[r.MonthNum]={ MonthNum:r.MonthNum, MonthName:r.MonthName };
      map[r.MonthNum][r['Business Unit']] = (map[r.MonthNum][r['Business Unit']]||0)+r.USD;
    });
    const rows = Object.values(map).sort((a,b)=>a.MonthNum-b.MonthNum);
    const buList = buFilter==='all'?BUS:[buFilter];
    const totals = Object.fromEntries(buList.map(bu=>[bu, rows.reduce((s,m)=>s+(m[bu]||0),0)]));
    const grand  = buList.reduce((s,bu)=>s+(totals[bu]||0),0);
    return { rows, totals, grand };
  },[monthlyAll,selectedYears,buFilter]);

  const buList = buFilter==='all'?BUS:[buFilter];
  const yc = yearContext(selectedYears, t);

  return (
    <div style={S.card}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
                    marginBottom:16, flexWrap:'wrap', gap:10 }}>
        <div>
          <div style={S.cardTitle}><Dot color="rgb(var(--brand-600))"/>{t('awsc.monthlyDetailByBu')}</div>
          {yc.multi && (
            <div style={{ fontSize:11, color:'#9ca3af', fontStyle:'italic', marginTop:-8 }}>
              {yc.note}
            </div>
          )}
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <span style={{ fontSize:11, color:'#6b7280', fontWeight:600 }}>{t('awsc.buLabel')}</span>
          <select style={S.select} value={buFilter} onChange={e=>setBuFilter(e.target.value)}>
            <option value="all">{t('awsc.allBu')}</option>
            {BUS.map(bu=><option key={bu}>{bu}</option>)}
          </select>
        </div>
      </div>
      <TableWrap>
        <thead><tr>
          <Th left>{t('awsc.monthCol')}</Th>
          {buList.map(bu=><Th key={bu}>{bu}</Th>)}
          <Th>{t('awsc.total')}</Th>
        </tr></thead>
        <tbody>
          {rows.length===0?<NoData/>:rows.map(m=>{
            const rt=buList.reduce((s,bu)=>s+(m[bu]||0),0);
            return (<tr key={m.MonthNum} style={{ borderBottom:'1px solid #f0ede8' }}>
              <Td left>{m.MonthName}</Td>
              {buList.map(bu=>(
                <td key={bu} style={{ ...S.tdR, color:BU_COLORS[bu] }}>
                  {fmt(m[bu]||0)}
                  {yc.multi && <div style={{ fontSize:10, color:'#9ca3af' }}>{fmt(yc.avg(m[bu]||0))}{yc.suffix}</div>}
                </td>
              ))}
              <td style={{ ...S.tdR, fontWeight:700 }}>
                {fmt(rt)}
                {yc.multi && <div style={{ fontSize:10, color:'#9ca3af', fontWeight:400 }}>{fmt(yc.avg(rt))}{yc.suffix}</div>}
              </td>
            </tr>);
          })}
        </tbody>
        <tfoot><tr style={{ background:'#f0ede8' }}>
          <td style={{ ...S.tdL, fontWeight:700, borderTop:'2px solid #d4cfc9' }}>{t('awsc.grandTotal')}</td>
          {buList.map(bu=>(
            <td key={bu} style={{ ...S.tdR, fontWeight:700, borderTop:'2px solid #d4cfc9' }}>
              {fmt(totals[bu]||0)}
              {yc.multi && <div style={{ fontSize:10, color:'#9ca3af', fontWeight:400 }}>{fmt(yc.avg(totals[bu]||0))}{yc.suffix}</div>}
            </td>
          ))}
          <td style={{ ...S.tdR, fontWeight:700, borderTop:'2px solid #d4cfc9' }}>
            {fmt(grand)}
            {yc.multi && <div style={{ fontSize:10, color:'#9ca3af', fontWeight:400 }}>{fmt(yc.avg(grand))}{yc.suffix}</div>}
          </td>
        </tr></tfoot>
      </TableWrap>
    </div>
  );
}

// ── Products Tab ──────────────────────────────────────────────────────────────
function ProductsTab({ prodMonth, selectedYears }) {
  const { t } = useLang();
  const [mFilter, setMFilter] = useState('all');
  const [pFilter, setPFilter] = useState('all');
  const [sort, setSort] = useState({ col:'total', dir:-1 });

  const handleSort = col => setSort(prev=>({ col, dir: prev.col===col?prev.dir*-1:-1 }));

  const { months, products, prods, grandTotals, grand } = useMemo(()=>{
    const rows = prodMonth.filter(r=>selectedYears.has(r.Year));
    const mMap ={};
    rows.forEach(r=>{ mMap[r.MonthNum]=r.MonthName; });
    const months = Object.entries(mMap).map(([n,name])=>({num:parseInt(n),name})).sort((a,b)=>a.num-b.num);
    const products = [...new Set(rows.map(r=>r['Product Code']))].sort();

    let filtered = rows;
    if(mFilter!=='all') filtered=filtered.filter(r=>r.MonthNum===parseInt(mFilter));
    if(pFilter!=='all') filtered=filtered.filter(r=>r['Product Code']===pFilter);

    const pMap={};
    filtered.forEach(r=>{
      const p=r['Product Code'];
      if(!pMap[p]) pMap[p]=Object.fromEntries(BUS.map(bu=>[bu,0]));
      pMap[p][r['Business Unit']]=(pMap[p][r['Business Unit']]||0)+r.USD;
    });
    let prods=Object.entries(pMap)
      .map(([name,v])=>({name,...v,total:BUS.reduce((s,bu)=>s+(v[bu]||0),0)}));

    const {col,dir}=sort;
    prods.sort((a,b)=>{
      const va=col==='name'?a.name:(a[col]||0);
      const vb=col==='name'?b.name:(b[col]||0);
      return typeof va==='string'?dir*va.localeCompare(vb):dir*(va-vb);
    });

    const grandTotals=Object.fromEntries(BUS.map(bu=>[bu,prods.reduce((s,p)=>s+(p[bu]||0),0)]));
    const grand=prods.reduce((s,p)=>s+p.total,0);
    return { months, products, prods, grandTotals, grand };
  },[prodMonth,selectedYears,mFilter,pFilter,sort]);

  // Bar strip selalu pakai 10 teratas berdasarkan total (independen dari sort tabel)
  const topByTotal = useMemo(()=>[...prods].sort((a,b)=>b.total-a.total).slice(0,10),[prods]);
  const maxT = (topByTotal[0]&&topByTotal[0].total)||1;

  const svcLabel = t('awsc.service');

  const SortTh = ({col,left,children}) => {
    const active=sort.col===col;
    const icon=active?(sort.dir===-1?'▼':'▲'):'⇅';
    return (
      <th onClick={()=>handleSort(col)} style={{ ...S.th,
        textAlign:left?'left':'right', cursor:'pointer', userSelect:'none',
        background:active?'#e0ecff':'#f0ede8',
        color:active?'rgb(var(--brand-600))':'#6b7280' }}>
        {children} <span style={{ fontSize:9, opacity:active?1:.35 }}>{icon}</span>
      </th>
    );
  };

  return (
    <div style={S.card}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
                    marginBottom:16, flexWrap:'wrap', gap:10 }}>
        <div style={S.cardTitle}><Dot color="rgb(var(--brand-600))"/>{t('awsc.topProducts')}</div>
        <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
          <span style={{ fontSize:11, color:'#6b7280', fontWeight:600 }}>{t('awsc.monthFilter')}</span>
          <select style={S.select} value={mFilter} onChange={e=>setMFilter(e.target.value)}>
            <option value="all">{t('awsc.all')}</option>
            {months.map(m=><option key={m.num} value={m.num}>{m.name}</option>)}
          </select>
          <span style={{ fontSize:11, color:'#6b7280', fontWeight:600 }}>{svcLabel}</span>
          <select style={S.select} value={pFilter} onChange={e=>setPFilter(e.target.value)}>
            <option value="all">{t('awsc.all')}</option>
            {products.map(p=><option key={p}>{p}</option>)}
          </select>
        </div>
      </div>
      {/* Bar strips */}
      <div style={{ marginBottom:18 }}>
        {topByTotal.map(p=>(
          <div key={p.name} style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
            <div style={{ fontSize:11, color:'#1a1a1a', width:160, overflow:'hidden',
              whiteSpace:'nowrap', textOverflow:'ellipsis', flexShrink:0 }} title={p.name}>{p.name}</div>
            <div style={{ flex:1, background:'#f0ede8', borderRadius:4, height:13,
              overflow:'hidden', border:'1px solid #e2ddd8' }}>
              <div style={{ height:'100%', borderRadius:4,
                background:'linear-gradient(90deg,rgb(var(--brand-600)),rgb(var(--brand-300)))',
                width:`${(p.total/maxT*100).toFixed(1)}%`, transition:'width .5s' }}/>
            </div>
            <div style={{ fontFamily:'"JetBrains Mono",monospace', fontSize:11,
              color:'#6b7280', width:70, textAlign:'right', flexShrink:0 }}>{fmtK(p.total)}</div>
          </div>
        ))}
      </div>
      <TableWrap>
        <thead><tr>
          <SortTh col="name" left>{svcLabel}</SortTh>
          {BUS.map(bu=><SortTh key={bu} col={bu}>{bu}</SortTh>)}
          <SortTh col="total">{t('awsc.total')}</SortTh>
        </tr></thead>
        <tbody>
          {prods.length===0?<NoData/>:prods.map(p=>(
            <tr key={p.name} style={{ borderBottom:'1px solid #f0ede8' }}>
              <Td left>{p.name}</Td>
              {BUS.map(bu=><Td key={bu} color={BU_COLORS[bu]}>{(p[bu]||0)>0.001?fmt(p[bu]):'-'}</Td>)}
              <Td bold>{fmt(p.total)}</Td>
            </tr>
          ))}
        </tbody>
        <tfoot><tr style={{ background:'#f0ede8' }}>
          <td style={{ ...S.tdL, fontWeight:700, borderTop:'2px solid #d4cfc9' }}>{t('awsc.grandTotal')}</td>
          {BUS.map(bu=><td key={bu} style={{ ...S.tdR, fontWeight:700, borderTop:'2px solid #d4cfc9' }}>{fmt(grandTotals[bu]||0)}</td>)}
          <td style={{ ...S.tdR, fontWeight:700, borderTop:'2px solid #d4cfc9' }}>{fmt(grand)}</td>
        </tr></tfoot>
      </TableWrap>
    </div>
  );
}

// ── Usage Tab (pivot tree: Service → Month → Usage Type) ──────────────────────
// Pivot bisa expand/collapse, DEFAULT collapsed. Grouping: Service → Month
// (mengikuti filter Month bila dipilih) → Usage Type (leaf). Tiap baris punya
// rincian BU + Usage Qty (kuantitas) + Total. Kuantitas dijumlah ke atas; karena
// satuan antar Usage Type bisa berbeda, total qty di level Service/Month bersifat
// indikatif (lihat catatan kecil di bawah judul).
function UsageTab({ usageMonth, selectedYears }) {
  const { t } = useLang();
  // tt: pakai terjemahan bila key ada di translations.js, jika belum → fallback.
  const tt = (key, fallback) => { const v = t(key); return (v && v !== key) ? v : fallback; };

  const [mFilter, setMFilter] = useState('all');
  const [pFilter, setPFilter] = useState('all');
  const [buFilter,setBuFilter]= useState('all');
  const [search, setSearch]   = useState('');
  const [openSvcs,  setOpenSvcs]  = useState(()=>new Set());   // default collapse
  const [openMonths,setOpenMonths]= useState(()=>new Set());

  const multiYear = selectedYears.size > 1;
  const zero = () => Object.fromEntries(BUS.map(bu=>[bu,0]));

  const { months, products, services, grandBU, grandQty, grand } = useMemo(()=>{
    const all = usageMonth.filter(r=>selectedYears.has(r.Year));
    const mMap={}; all.forEach(r=>{ mMap[r.MonthNum]=r.MonthName; });
    const months = Object.entries(mMap).map(([n,name])=>({num:parseInt(n),name})).sort((a,b)=>a.num-b.num);
    const products = [...new Set(all.map(r=>r['Product Code']))].sort();

    let rows = all;
    if(mFilter!=='all') rows=rows.filter(r=>r.MonthNum===parseInt(mFilter));
    if(pFilter!=='all') rows=rows.filter(r=>r['Product Code']===pFilter);
    if(buFilter!=='all') rows=rows.filter(r=>r['Business Unit']===buFilter);
    const q=search.trim().toLowerCase();
    if(q) rows=rows.filter(r=>
      (r['Product Code']||'').toLowerCase().includes(q) ||
      (r['Usage Type']||'').toLowerCase().includes(q) ||
      decodeUsageType(r['Usage Type']||'').label.toLowerCase().includes(q));

    // Bangun pohon: service → month → usage type
    // CATATAN qty: backend mengembalikan 1 baris per (prod, usageType, month, BU).
    // UsageQty di tiap baris = total qty untuk kombinasi (prod, usageType, month) dari DB —
    // nilainya sama di semua baris BU untuk usageType yang sama. Kita hanya ambil qty
    // dari BU baris PERTAMA, bukan dijumlah per BU agar tidak terganda.
    const svcMap={};
    // Pelacak qty per kunci unik (Year-MonthNum-ProductCode-UsageType) supaya tidak dobel.
    const qtySeen={};
    rows.forEach(r=>{
      const svc=r['Product Code']||'(none)';
      if(!svcMap[svc]) svcMap[svc]={ name:svc, bu:zero(), qty:0, total:0, months:{} };
      const s=svcMap[svc];
      const mKey=r.Year+'-'+r.MonthNum;
      if(!s.months[mKey]) s.months[mKey]={ key:mKey, year:r.Year, monthNum:r.MonthNum, monthName:r.MonthName, bu:zero(), qty:0, total:0, usages:{} };
      const m=s.months[mKey];
      const ut=r['Usage Type']||'(none)';
      if(!m.usages[ut]) m.usages[ut]={ name:ut, bu:zero(), qty:0, total:0 };
      const u=m.usages[ut];
      const buName=r['Business Unit']; const usd=r.USD||0;
      // Qty hanya dihitung sekali per (year-month-svc-usageType), bukan per BU row.
      const qtyKey=r.Year+'-'+r.MonthNum+'-'+svc+'-'+ut;
      const qy = qtySeen[qtyKey] ? 0 : (r.UsageQty||0);
      if(!qtySeen[qtyKey] && (r.UsageQty||0) > 0) qtySeen[qtyKey]=true;
      u.bu[buName]=(u.bu[buName]||0)+usd; u.qty+=qy; u.total+=usd;
      m.bu[buName]=(m.bu[buName]||0)+usd; m.qty+=qy; m.total+=usd;
      s.bu[buName]=(s.bu[buName]||0)+usd; s.qty+=qy; s.total+=usd;
    });

    const services = Object.values(svcMap)
      .sort((a,b)=>b.total-a.total)
      .map(s=>({ ...s,
        months: Object.values(s.months)
          .sort((a,b)=> a.year-b.year || a.monthNum-b.monthNum)
          .map(m=>({ ...m, usages: Object.values(m.usages).sort((a,b)=>b.total-a.total) })) }));

    const grandBU=zero(); let grandQty=0;
    services.forEach(s=>{ BUS.forEach(bu=>{ grandBU[bu]+=s.bu[bu]||0; }); grandQty+=s.qty; });
    const grand=BUS.reduce((acc,bu)=>acc+grandBU[bu],0);
    return { months, products, services, grandBU, grandQty, grand };
  },[usageMonth,selectedYears,mFilter,pFilter,buFilter,search]);

  const svcLabel = t('awsc.service');
  const qtyLabel = tt('awsc.usageQty', 'Usage Qty');

  const monthLabel = m => multiYear ? `${m.monthName} ${m.year}` : m.monthName;
  const mKey = (svc,key)=>svc+'||'+key;
  const toggle = (set,setter,key)=>setter(prev=>{ const n=new Set(prev); n.has(key)?n.delete(key):n.add(key); return n; });

  const expandAll = () => {
    const ss=new Set(), ms=new Set();
    services.forEach(s=>{ ss.add(s.name); s.months.forEach(m=>ms.add(mKey(s.name,m.key))); });
    setOpenSvcs(ss); setOpenMonths(ms);
  };
  const collapseAll = () => { setOpenSvcs(new Set()); setOpenMonths(new Set()); };

  const Caret = ({open}) => (
    <span style={{ display:'inline-block', width:14, color:'#9ca3af', fontSize:10,
      transform:open?'rotate(90deg)':'none', transition:'transform .12s' }}>▶</span>
  );
  const BuCells = (bumap,{bold}={}) => BUS.map(bu=>(
    <td key={bu} style={{ ...S.tdR, color:BU_COLORS[bu], fontWeight:bold?700:400 }}>
      {(bumap[bu]||0)>0.001?fmt(bumap[bu]):'-'}
    </td>
  ));
  const QtyCell = (qty,{bold,usageType}={}) => (
    <td style={{ ...S.tdR, color:"#6b7280", fontWeight:bold?700:400 }}>
      {isQtyNA(usageType,qty) ? "n/a" : fmtQty(qty)}
    </td>
  );

  return (
    <div style={S.card}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
                    marginBottom:6, flexWrap:'wrap', gap:10 }}>
        <div style={S.cardTitle}><Dot color="rgb(var(--brand-600))"/>{t('awsc.usageDetail')}</div>
        <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
          <button onClick={expandAll}   style={{ ...S.select, cursor:'pointer' }}>{t('awsc.expandAll')}</button>
          <button onClick={collapseAll} style={{ ...S.select, cursor:'pointer' }}>{t('awsc.collapseAll')}</button>
          <input
            type="text"
            value={search}
            onChange={e=>setSearch(e.target.value)}
            placeholder={t('awsc.searchService')}
            style={{ ...S.select, width:200, cursor:'text' }}
          />
          {search && (
            <button onClick={()=>setSearch('')}
              style={{ fontSize:14, lineHeight:1, padding:'4px 8px', borderRadius:8,
                border:'1px solid #d4cfc9', background:'#f0ede8', color:'#6b7280',
                cursor:'pointer', fontFamily:'inherit' }}>✕</button>
          )}
          <span style={{ fontSize:11, color:'#6b7280', fontWeight:600 }}>{t('awsc.monthFilter')}</span>
          <select style={S.select} value={mFilter} onChange={e=>setMFilter(e.target.value)}>
            <option value="all">{t('awsc.all')}</option>
            {months.map(m=><option key={m.num} value={m.num}>{m.name}</option>)}
          </select>
          <span style={{ fontSize:11, color:'#6b7280', fontWeight:600 }}>{svcLabel}</span>
          <select style={S.select} value={pFilter} onChange={e=>setPFilter(e.target.value)}>
            <option value="all">{t('awsc.all')}</option>
            {products.map(p=><option key={p}>{p}</option>)}
          </select>
          <span style={{ fontSize:11, color:'#6b7280', fontWeight:600 }}>{t('awsc.buLabel')}</span>
          <select style={S.select} value={buFilter} onChange={e=>setBuFilter(e.target.value)}>
            <option value="all">{t('awsc.all')}</option>
            {BUS.map(bu=><option key={bu}>{bu}</option>)}
          </select>
        </div>
      </div>
      <div style={{ fontSize:11, color:'#9ca3af', fontStyle:'italic', marginBottom:16 }}>
        {tt('awsc.usagePivotNote',
          'Klik baris untuk expand/collapse. Pengelompokan: Service → Month → Usage Type. ' +
          'Usage Qty dijumlahkan; karena satuan tiap Usage Type bisa berbeda, total qty di tingkat Service/Month bersifat indikatif. ' +
          'Reserved Instance (HeavyUsage) tidak melaporkan kuantitas di Cost Explorer → ditandai n/a.')}
      </div>
      <TableWrap>
        <thead><tr>
          <Th left>{svcLabel}</Th>
          {BUS.map(bu=><Th key={bu}>{bu}</Th>)}
          <Th>{qtyLabel}</Th>
          <Th>{t('awsc.total')}</Th>
        </tr></thead>
        <tbody>
          {services.length===0 ? <NoData/> : services.map(s=>{
            const sOpen=openSvcs.has(s.name);
            return (
              <React.Fragment key={s.name}>
                {/* Service row */}
                <tr onClick={()=>toggle(openSvcs,setOpenSvcs,s.name)}
                    style={{ borderBottom:'1px solid #f0ede8', cursor:'pointer', background:'#f0ede8' }}>
                  <td style={{ ...S.tdL, fontWeight:700, color:'#1a1a1a' }}>
                    <Caret open={sOpen}/> {s.name}
                    <span style={{ fontSize:10, color:'#9ca3af', marginLeft:6, fontWeight:400 }}>
                      ({s.months.length} {tt('awsc.monthsWord','month')})
                    </span>
                  </td>
                  {BuCells(s.bu,{bold:true})}
                  {QtyCell(s.qty,{bold:true})}
                  <td style={{ ...S.tdR, fontWeight:800 }}>{fmt(s.total)}</td>
                </tr>
                {/* Month rows */}
                {sOpen && s.months.map(m=>{
                  const mk=mKey(s.name,m.key), mOpen=openMonths.has(mk);
                  return (
                    <React.Fragment key={mk}>
                      <tr onClick={()=>toggle(openMonths,setOpenMonths,mk)}
                          style={{ borderBottom:'1px solid #f5f3f0', cursor:'pointer', background:'#faf9f7' }}>
                        <td style={{ ...S.tdL, paddingLeft:30, fontWeight:600, color:'#1a1a1a' }}>
                          <Caret open={mOpen}/> {monthLabel(m)}
                          <span style={{ fontSize:10, color:'#9ca3af', marginLeft:6, fontWeight:400 }}>
                            ({m.usages.length})
                          </span>
                        </td>
                        {BuCells(m.bu)}
                        {QtyCell(m.qty)}
                        <td style={{ ...S.tdR, fontWeight:700 }}>{fmt(m.total)}</td>
                      </tr>
                      {/* Usage type leaves */}
                      {mOpen && m.usages.map((u,ui)=>(
                        <tr key={mk+'::'+ui} style={{ borderBottom:'1px solid #f5f3f0' }}>
                          <UsageTypeCell code={u.name} padLeft={56} />
                          {BuCells(u.bu)}
                          {QtyCell(u.qty,{usageType:u.name})}
                          <td style={{ ...S.tdR }}>{fmt(u.total)}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </React.Fragment>
            );
          })}
        </tbody>
        <tfoot><tr style={{ background:'#f0ede8' }}>
          <td style={{ ...S.tdL, fontWeight:700, borderTop:'2px solid #d4cfc9' }}>{t('awsc.grandTotal')}</td>
          {BUS.map(bu=><td key={bu} style={{ ...S.tdR, fontWeight:700, borderTop:'2px solid #d4cfc9' }}>{fmt(grandBU[bu]||0)}</td>)}
          <td style={{ ...S.tdR, fontWeight:700, borderTop:'2px solid #d4cfc9' }}>{fmtQty(grandQty)}</td>
          <td style={{ ...S.tdR, fontWeight:700, borderTop:'2px solid #d4cfc9' }}>{fmt(grand)}</td>
        </tr></tfoot>
      </TableWrap>
    </div>
  );
}

// ── Daily Usage Tab (tree: Day → Service → Usage Type) ────────────────────────
// Sumber: data.daily_usage (per hari). Bisa expand/collapse tiap level.
// CATATAN: data lama yang diimport sebelum fitur harian punya day=1, sehingga
// akan muncul terkumpul di tanggal 1. Upload/fetch baru akan terisi per hari.
function DailyUsageTab({ dailyUsage, selectedYears }) {
  const { t } = useLang();
  const tt = (key, fallback) => { const v = t(key); return (v && v !== key) ? v : fallback; };
  const [mFilter, setMFilter] = useState('all');
  const [search, setSearch]   = useState('');
  const [openDays, setOpenDays] = useState(()=>new Set());
  const [openSvcs, setOpenSvcs] = useState(()=>new Set());

  const multiYear = selectedYears.size > 1;
  const zero = () => Object.fromEntries(BUS.map(bu=>[bu,0]));

  const { days, months, grandBU, grandQty, grand } = useMemo(()=>{
    const all = (dailyUsage||[]).filter(r=>selectedYears.has(r.Year));
    const mMap={}; all.forEach(r=>{ mMap[r.MonthNum]=r.MonthName; });
    const months = Object.entries(mMap).map(([n,name])=>({num:parseInt(n),name})).sort((a,b)=>a.num-b.num);

    let rows = all;
    if(mFilter!=='all') rows=rows.filter(r=>r.MonthNum===parseInt(mFilter));
    const q=search.trim().toLowerCase();
    if(q) rows=rows.filter(r=>
      (r['Product Code']||'').toLowerCase().includes(q) ||
      (r['Usage Type']||'').toLowerCase().includes(q) ||
      decodeUsageType(r['Usage Type']||'').label.toLowerCase().includes(q));

    const dayMap={};
    const qtySeen={};  // key: year-month-day-svc-usageType, qty hanya dihitung sekali (tidak per BU)
    rows.forEach(r=>{
      const dkey=r.Year+'-'+r.MonthNum+'-'+r.Day;
      if(!dayMap[dkey]) dayMap[dkey]={ key:dkey, year:r.Year, monthNum:r.MonthNum, monthName:r.MonthName, day:r.Day, bu:zero(), qty:0, total:0, services:{} };
      const d=dayMap[dkey];
      const svc=r['Product Code']||'(none)';
      if(!d.services[svc]) d.services[svc]={ name:svc, bu:zero(), qty:0, total:0, usages:{} };
      const s=d.services[svc];
      const ut=r['Usage Type']||'(none)';
      if(!s.usages[ut]) s.usages[ut]={ name:ut, bu:zero(), qty:0, total:0 };
      const u=s.usages[ut];
      const buName=r['Business Unit']; const usd=r.USD||0;
      // Qty hanya dihitung sekali per (day-svc-usageType), bukan per BU row.
      const qtyKey=dkey+'-'+svc+'-'+ut;
      const qy = qtySeen[qtyKey] ? 0 : (r.UsageQty||0);
      if(!qtySeen[qtyKey] && (r.UsageQty||0) > 0) qtySeen[qtyKey]=true;
      u.bu[buName]=(u.bu[buName]||0)+usd; u.qty+=qy; u.total+=usd;
      s.bu[buName]=(s.bu[buName]||0)+usd; s.qty+=qy; s.total+=usd;
      d.bu[buName]=(d.bu[buName]||0)+usd; d.qty+=qy; d.total+=usd;
    });

    const days=Object.values(dayMap)
      .sort((a,b)=> a.year-b.year || a.monthNum-b.monthNum || a.day-b.day)
      .map(d=>({ ...d,
        services: Object.values(d.services).sort((a,b)=>b.total-a.total)
          .map(s=>({ ...s, usages: Object.values(s.usages).sort((a,b)=>b.total-a.total) })) }));

    const grandBU=zero(); let grandQty=0;
    days.forEach(d=>{ BUS.forEach(bu=>{ grandBU[bu]+=d.bu[bu]||0; }); grandQty+=d.qty; });
    const grand=BUS.reduce((acc,bu)=>acc+grandBU[bu],0);
    return { days, months, grandBU, grandQty, grand };
  },[dailyUsage,selectedYears,mFilter,search]);

  const dayLabel = d => {
    const dd=String(d.day).padStart(2,'0');
    return multiYear ? `${dd}-${d.monthName} ${d.year}` : `${dd}-${d.monthName}`;
  };
  const svcKey = (dkey,name)=>dkey+'||'+name;
  const toggle = (set,setter,key)=>setter(prev=>{ const n=new Set(prev); n.has(key)?n.delete(key):n.add(key); return n; });

  const expandAll = () => {
    const ds=new Set(), ss=new Set();
    days.forEach(d=>{ ds.add(d.key); d.services.forEach(s=>ss.add(svcKey(d.key,s.name))); });
    setOpenDays(ds); setOpenSvcs(ss);
  };
  const collapseAll = () => { setOpenDays(new Set()); setOpenSvcs(new Set()); };

  const Caret = ({open}) => (
    <span style={{ display:'inline-block', width:14, color:'#9ca3af', fontSize:10,
      transform:open?'rotate(90deg)':'none', transition:'transform .12s' }}>▶</span>
  );
  const BuCells = (bumap,{bold}={}) => BUS.map(bu=>(
    <td key={bu} style={{ ...S.tdR, color:BU_COLORS[bu], fontWeight:bold?700:400 }}>
      {(bumap[bu]||0)>0.001?fmt(bumap[bu]):'-'}
    </td>
  ));
  const QtyCell = (qty,{bold,usageType}={}) => (
    <td style={{ ...S.tdR, color:"#6b7280", fontWeight:bold?700:400 }}>
      {isQtyNA(usageType,qty) ? "n/a" : fmtQty(qty)}
    </td>
  );

  return (
    <div style={S.card}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
                    marginBottom:6, flexWrap:'wrap', gap:10 }}>
        <div style={S.cardTitle}><Dot color="rgb(var(--brand-600))"/>{t('awsc.dailyUsage')}</div>
        <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
          <button onClick={expandAll} style={{ ...S.select, cursor:'pointer' }}>{t('awsc.expandAll')}</button>
          <button onClick={collapseAll} style={{ ...S.select, cursor:'pointer' }}>{t('awsc.collapseAll')}</button>
          <input type="text" value={search} onChange={e=>setSearch(e.target.value)}
            placeholder={t('awsc.searchService')} style={{ ...S.select, width:200, cursor:'text' }} />
          {search && (
            <button onClick={()=>setSearch('')} style={{ fontSize:14, lineHeight:1, padding:'4px 8px',
              borderRadius:8, border:'1px solid #d4cfc9', background:'#f0ede8', color:'#6b7280',
              cursor:'pointer', fontFamily:'inherit' }}>✕</button>
          )}
          <span style={{ fontSize:11, color:'#6b7280', fontWeight:600 }}>{t('awsc.monthFilter')}</span>
          <select style={S.select} value={mFilter} onChange={e=>setMFilter(e.target.value)}>
            <option value="all">{t('awsc.all')}</option>
            {months.map(m=><option key={m.num} value={m.num}>{m.name}</option>)}
          </select>
        </div>
      </div>
      <div style={{ fontSize:11, color:'#9ca3af', fontStyle:'italic', marginBottom:16 }}>
        {t('awsc.dailyTreeNote')}
      </div>
      <TableWrap>
        <thead><tr>
          <Th left>{t('awsc.dayCol')}</Th>
          {BUS.map(bu=><Th key={bu}>{bu}</Th>)}
          <Th>{tt('awsc.usageQty','Usage Qty')}</Th>
          <Th>{t('awsc.total')}</Th>
        </tr></thead>
        <tbody>
          {days.length===0 ? <NoData/> : days.map(d=>{
            const dOpen=openDays.has(d.key);
            return (
              <React.Fragment key={d.key}>
                {/* Day row */}
                <tr onClick={()=>toggle(openDays,setOpenDays,d.key)}
                    style={{ borderBottom:'1px solid #f0ede8', cursor:'pointer', background:'#f0ede8' }}>
                  <td style={{ ...S.tdL, fontWeight:700, color:'#1a1a1a' }}>
                    <Caret open={dOpen}/> {dayLabel(d)}
                    <span style={{ fontSize:10, color:'#9ca3af', marginLeft:6, fontWeight:400 }}>
                      ({d.services.length} {t('awsc.service').toLowerCase()})
                    </span>
                  </td>
                  {BuCells(d.bu,{bold:true})}
                  {QtyCell(d.qty,{bold:true})}
                  <td style={{ ...S.tdR, fontWeight:800 }}>{fmt(d.total)}</td>
                </tr>
                {/* Services */}
                {dOpen && d.services.map(s=>{
                  const sk=svcKey(d.key,s.name), sOpen=openSvcs.has(sk);
                  return (
                    <React.Fragment key={sk}>
                      <tr onClick={()=>toggle(openSvcs,setOpenSvcs,sk)}
                          style={{ borderBottom:'1px solid #f5f3f0', cursor:'pointer', background:'#faf9f7' }}>
                        <td style={{ ...S.tdL, paddingLeft:30, fontWeight:600, color:'#1a1a1a' }}>
                          <Caret open={sOpen}/> {s.name}
                          <span style={{ fontSize:10, color:'#9ca3af', marginLeft:6, fontWeight:400 }}>
                            ({s.usages.length})
                          </span>
                        </td>
                        {BuCells(s.bu)}
                        {QtyCell(s.qty)}
                        <td style={{ ...S.tdR, fontWeight:700 }}>{fmt(s.total)}</td>
                      </tr>
                      {/* Usage types (leaf) */}
                      {sOpen && s.usages.map((u,ui)=>(
                        <tr key={sk+'::'+ui} style={{ borderBottom:'1px solid #f5f3f0' }}>
                          <UsageTypeCell code={u.name} padLeft={56} />
                          {BuCells(u.bu)}
                          {QtyCell(u.qty,{usageType:u.name})}
                          <td style={{ ...S.tdR }}>{fmt(u.total)}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </React.Fragment>
            );
          })}
        </tbody>
        <tfoot><tr style={{ background:'#f0ede8' }}>
          <td style={{ ...S.tdL, fontWeight:700, borderTop:'2px solid #d4cfc9' }}>{t('awsc.grandTotal')}</td>
          {BUS.map(bu=><td key={bu} style={{ ...S.tdR, fontWeight:700, borderTop:'2px solid #d4cfc9' }}>{fmt(grandBU[bu]||0)}</td>)}
          <td style={{ ...S.tdR, fontWeight:700, borderTop:'2px solid #d4cfc9' }}>{fmtQty(grandQty)}</td>
          <td style={{ ...S.tdR, fontWeight:700, borderTop:'2px solid #d4cfc9' }}>{fmt(grand)}</td>
        </tr></tfoot>
      </TableWrap>
    </div>
  );
}

// ── Analytics Tab ─────────────────────────────────────────────────────────────
// Lapisan "insight" (bukan breakdown mentah): tren MoM, top movers, Pareto,
// deteksi anomali, run-rate/proyeksi, pergeseran mix BU, unit economics.
// Murni dihitung dari data yang sudah di-load (monthly_all/prod_month/usage_month)
// — tidak butuh endpoint baru. Semua agregasi dihitung di JS (useMemo).
function AnalyticsTab({ monthlyAll, prodMonth, usageMonth, selectedYears }) {
  const { t, lang } = useLang();
  const L = (id, en) => (lang === 'en' ? en : id);
  const pctFmt = v => (v == null ? '—' : (v >= 0 ? '+' : '') + v.toFixed(1) + '%');
  const pctColor = v => (v == null ? '#6b7280' : v > 0 ? '#dc2626' : v < 0 ? '#15803d' : '#6b7280');
  const [openMover, setOpenMover] = useState(null); // produk yg di-drilldown (breakdown usage type & BU)

  // Seri periode kronologis (total lintas BU per bulan) ───────────────────────
  const periods = useMemo(() => {
    const rows = monthlyAll.filter(r => selectedYears.has(r.Year));
    const map = new Map();
    rows.forEach(r => {
      const ym = r.Year * 100 + r.MonthNum;
      const e = map.get(ym) || { ym, yr: r.Year, m: r.MonthNum, name: r.MonthName, total: 0 };
      e.total += Math.max(0, r.USD || 0);
      map.set(ym, e);
    });
    return [...map.values()].sort((a, b) => a.ym - b.ym);
  }, [monthlyAll, selectedYears]);

  const multiYr = new Set(periods.map(p => p.yr)).size > 1;
  const pLabel = p => (multiYr ? `${MONTH_SHORT[p.m - 1]} '${String(p.yr).slice(2)}` : (p.name || MONTH_SHORT[p.m - 1]));

  // Headline KPI: MoM, run-rate proyeksi, BU tumbuh tercepat ───────────────────
  const head = useMemo(() => {
    const now = new Date();
    const curYM = now.getFullYear() * 100 + (now.getMonth() + 1);
    // Kartu KPI berbasis bulan PENUH: buang bulan kalender berjalan (belum genap).
    const fp = (periods.length && periods[periods.length - 1].ym === curYM) ? periods.slice(0, -1) : periods;
    const n = fp.length;
    const last = n ? fp[n - 1] : null;
    const prev = n > 1 ? fp[n - 2] : null;
    const mom = last && prev && prev.total > 0 ? ((last.total - prev.total) / prev.total) * 100 : null;
    const grand = fp.reduce((s, p) => s + p.total, 0);

    let forecast = null, feYear = null, actualSoFar = null;
    if (last) {
      feYear = last.yr;
      const yrP = fp.filter(p => p.yr === feYear);
      actualSoFar = yrP.reduce((s, p) => s + p.total, 0);
      forecast = yrP.length ? (actualSoFar / yrP.length) * 12 : null;
    }

    let topBU = null;
    if (last && prev) {
      const at = ym => {
        const o = {}; BUS.forEach(b => (o[b] = 0));
        monthlyAll.filter(r => r.Year * 100 + r.MonthNum === ym)
          .forEach(r => { o[r['Business Unit']] = (o[r['Business Unit']] || 0) + Math.max(0, r.USD || 0); });
        return o;
      };
      const a = at(prev.ym), b = at(last.ym);
      BUS.forEach(bu => {
        if (a[bu] > 0) { const g = ((b[bu] - a[bu]) / a[bu]) * 100; if (!topBU || g > topBU.g) topBU = { bu, g }; }
      });
    }
    return { n, last, prev, mom, grand, forecast, feYear, actualSoFar, topBU };
  }, [periods, monthlyAll]);

  // Tren total + rata-rata bergerak 3 bulan ────────────────────────────────────
  const trend = useMemo(() => {
    const labels = periods.map(pLabel);
    const totals = periods.map(p => p.total);
    const ma = totals.map((_, i) => {
      const w = totals.slice(Math.max(0, i - 2), i + 1);
      return w.reduce((s, v) => s + v, 0) / w.length;
    });
    return { labels, totals, ma };
  }, [periods]);

  // Dua bulan PENUH terakhir: buang bulan kalender berjalan (belum lengkap) ─────
  // mis. jika Juli sedang berjalan → current = Jun, prev = May.
  const fullPair = useMemo(() => {
    if (!periods.length) return { cur: null, prev: null };
    const now = new Date();
    const curYM = now.getFullYear() * 100 + (now.getMonth() + 1);
    let list = periods;
    if (list[list.length - 1].ym === curYM) list = list.slice(0, -1); // buang bulan berjalan
    return { cur: list[list.length - 1] || null, prev: list[list.length - 2] || null };
  }, [periods]);

  // Top movers: produk dgn perubahan $ terbesar (dua bulan PENUH terakhir) ──────
  const movers = useMemo(() => {
    const { cur: LC, prev: LP } = fullPair;
    if (!LC || !LP) return [];
    const sum = ym => {
      const o = {};
      prodMonth.filter(r => r.Year * 100 + r.MonthNum === ym)
        .forEach(r => { const k = r['Product Code'] || '—'; o[k] = (o[k] || 0) + Math.max(0, r.USD || 0); });
      return o;
    };
    const a = sum(LP.ym), b = sum(LC.ym);
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    const arr = [...keys].map(k => {
      const prev = a[k] || 0, cur = b[k] || 0;
      return { k, prev, cur, delta: cur - prev, pct: prev > 0 ? ((cur - prev) / prev) * 100 : null };
    }).filter(x => Math.abs(x.delta) > 0.01);
    arr.sort((x, y) => Math.abs(y.delta) - Math.abs(x.delta));
    return arr.slice(0, 8);
  }, [prodMonth, fullPair]);

  // Breakdown tiap produk → per Usage Type & per BU (dua bulan penuh yg sama) ────
  const moverDetail = useMemo(() => {
    const { cur: LC, prev: LP } = fullPair;
    const res = {};
    if (!LC || !LP || !usageMonth || !usageMonth.length) return res;
    const build = (dimField) => {
      const map = {};
      const add = (ym, slot) => {
        usageMonth.filter(r => r.Year * 100 + r.MonthNum === ym).forEach(r => {
          const prod = r['Product Code'] || '—';
          const ent  = r[dimField] || '—';
          const p = map[prod] || (map[prod] = {});
          const e = p[ent] || (p[ent] = { name: ent, prev: 0, cur: 0 });
          e[slot] += Math.max(0, r.USD || 0);
        });
      };
      add(LP.ym, 'prev'); add(LC.ym, 'cur');
      return map;
    };
    const utMap = build('Usage Type');
    const buMap = build('Business Unit');
    const finalize = (m) => Object.values(m)
      .map(e => ({ ...e, delta: e.cur - e.prev, pct: e.prev > 0 ? ((e.cur - e.prev) / e.prev) * 100 : null }))
      .filter(e => Math.abs(e.delta) > 0.01 || e.prev > 0.01 || e.cur > 0.01)
      .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
    new Set([...Object.keys(utMap), ...Object.keys(buMap)]).forEach(pr => {
      res[pr] = { ut: finalize(utMap[pr] || {}), bu: finalize(buMap[pr] || {}) };
    });
    return res;
  }, [usageMonth, fullPair]);

  // Pareto: total per produk lintas periode terpilih + kumulatif % ─────────────
  const pareto = useMemo(() => {
    const o = {};
    prodMonth.filter(r => selectedYears.has(r.Year))
      .forEach(r => { const k = r['Product Code'] || '—'; o[k] = (o[k] || 0) + Math.max(0, r.USD || 0); });
    const arr = Object.entries(o).map(([k, v]) => ({ k, v })).sort((a, b) => b.v - a.v);
    const grand = arr.reduce((s, x) => s + x.v, 0);
    const top = arr.slice(0, 10);
    let cum = 0;
    const withCum = top.map(x => { cum += x.v; return { ...x, cumPct: grand > 0 ? (cum / grand) * 100 : 0 }; });
    // berapa produk untuk mencapai 80%
    let c = 0, n80 = 0;
    for (const x of arr) { c += x.v; n80++; if (grand > 0 && c / grand >= 0.8) break; }
    return { rows: withCum, grand, n80, total: arr.length };
  }, [prodMonth, selectedYears]);

  // Deteksi anomali (per-service & per-BU) — target: bulan PENUH year-to-date pada
  // TAHUN TERPILIH (buang bulan berjalan, mis. Juli). Baseline = rata-rata 3 bln
  // sebelumnya; BOLEH menyeberang ke tahun sebelumnya (Okt–Des) sehingga Januari
  // pun bisa dievaluasi. Ditandai bila cur > 1.4× baseline DAN kenaikan ≥ $ambang.
  const ANOM_MULT = 1.4, ANOM_MIN_DELTA = 100, ANOM_BASE_MONTHS = 6;
  const anomalies = useMemo(() => {
    const now = new Date();
    const curYM = now.getFullYear() * 100 + (now.getMonth() + 1); // bulan berjalan (belum penuh)
    // Urutan bulan lintas tahun → utk baseline trailing 3-bln (boleh mundur ke tahun lalu).
    const allYM = [...new Set([...prodMonth, ...monthlyAll].map(r => r.Year * 100 + r.MonthNum))].sort((a, b) => a - b);
    const idxOf = new Map(allYM.map((ym, i) => [ym, i]));
    const labelByYm = new Map(periods.map(p => [p.ym, pLabel(p)]));
    const ymLabel = ym => labelByYm.get(ym) || MONTH_SHORT[(ym % 100) - 1];
    // Bulan target: tahun terpilih, bulan PENUH (YTD), buang bulan berjalan.
    const targetYMs = allYM.filter(ym => selectedYears.has(Math.floor(ym / 100)) && ym !== curYM);
    const out = [];
    const scan = (dataset, field, dim) => {
      const ent = new Map(); // name → Map(ym → usd); TANPA filter tahun agar baseline bisa lintas tahun
      dataset.forEach(r => {
        const name = r[field] || '—';
        const ym = r.Year * 100 + r.MonthNum;
        const m = ent.get(name) || new Map();
        m.set(ym, (m.get(ym) || 0) + Math.max(0, r.USD || 0));
        ent.set(name, m);
      });
      ent.forEach((m, name) => {
        targetYMs.forEach(ym => {
          const i = idxOf.get(ym);
          if (i == null || i < ANOM_BASE_MONTHS) return; // butuh N bln sebelumnya (boleh lintas tahun)
          let _sum = 0; for (let j = 1; j <= ANOM_BASE_MONTHS; j++) _sum += (m.get(allYM[i - j]) || 0);
          const avg = _sum / ANOM_BASE_MONTHS;
          const cur = m.get(ym) || 0;
          if (avg > 0 && cur > avg * ANOM_MULT && (cur - avg) >= ANOM_MIN_DELTA) {
            out.push({ ym, label: ymLabel(ym), dim, name, base: avg, cur, ratio: cur / avg, pct: ((cur - avg) / avg) * 100, delta: cur - avg });
          }
        });
      });
    };
    scan(prodMonth, 'Product Code', L('Service', 'Service'));
    scan(monthlyAll, 'Business Unit', 'BU');
    out.sort((a, b) => (b.ym - a.ym) || (Math.abs(b.delta) - Math.abs(a.delta)));
    return out.slice(0, 50);
  }, [prodMonth, monthlyAll, periods, selectedYears]); // eslint-disable-line

  // Pergeseran mix BU (100% stacked per periode) ───────────────────────────────
  const mix = useMemo(() => {
    const labels = periods.map(pLabel);
    const datasets = BUS.map(bu => ({
      label: bu,
      data: periods.map(p => {
        const tot = p.total || 1;
        const v = monthlyAll.filter(r => r.Year * 100 + r.MonthNum === p.ym && r['Business Unit'] === bu)
          .reduce((s, r) => s + Math.max(0, r.USD || 0), 0);
        return (v / tot) * 100;
      }),
      backgroundColor: BU_COLORS[bu] + 'cc', borderColor: BU_COLORS[bu], borderWidth: 1,
    }));
    return { labels, datasets };
  }, [periods, monthlyAll]);

  // Unit economics: USD per UsageQty untuk usage type ber-qty valid ────────────
  const unit = useMemo(() => {
    if (!usageMonth || !usageMonth.length) return { rows: [], hasQty: false };
    const rows = usageMonth.filter(r => selectedYears.has(r.Year));
    const agg = {};
    rows.forEach(r => {
      const ut = r['Usage Type'] || '';
      const qty = Number(r.UsageQty || 0);
      if (isQtyNA(ut, qty) || Math.abs(qty) < 1e-6) return;
      const k = ut;
      const e = agg[k] || { k, usd: 0, qty: 0 };
      e.usd += Math.max(0, r.USD || 0); e.qty += qty;
      agg[k] = e;
    });
    const arr = Object.values(agg)
      .filter(x => x.qty > 0 && x.usd > 0)
      .map(x => ({ ...x, rate: x.usd / x.qty, label: decodeUsageType(x.k).label }))
      .sort((a, b) => b.usd - a.usd)
      .slice(0, 8);
    return { rows: arr, hasQty: arr.length > 0 };
  }, [usageMonth, selectedYears]);

  if (!periods.length) {
    return (
      <div style={S.card}>
        <div style={{ textAlign: 'center', color: '#9ca3af', padding: 40, fontSize: 13 }}>
          {L('Tidak ada data untuk periode terpilih.', 'No data for the selected period.')}
        </div>
      </div>
    );
  }

  const kpi = (label, value, sub, color) => (
    <div style={{ ...S.card, background: '#f8f6f3', padding: '14px 16px' }}>
      <div style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1, color: '#6b7280', marginBottom: 6 }}>{label}</div>
      <div style={{ fontFamily: '"JetBrains Mono",monospace', fontSize: 20, fontWeight: 700, color: color || '#1a1a1a' }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 3 }}>{sub}</div>}
    </div>
  );

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {/* Headline KPI */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12 }} className="aws-an-kpi">
        {kpi(L('Total periode', 'Period total'), fmt(head.grand),
          `${head.n} ${L('bulan', 'months')}`)}
        {kpi(L('MoM terakhir', 'Latest MoM'), pctFmt(head.mom),
          head.last ? `${pLabel(head.last)} ${L('vs', 'vs')} ${head.prev ? pLabel(head.prev) : '—'}` : '', pctColor(head.mom))}
        {kpi(L('Proyeksi akhir thn', 'Year-end forecast'),
          head.forecast != null ? fmtK(head.forecast) : '—',
          head.feYear ? `${L('run-rate', 'run-rate')} ${head.feYear}` : '')}
        {kpi(L('BU tumbuh tercepat', 'Fastest-growing BU'),
          head.topBU ? head.topBU.bu : '—',
          head.topBU ? pctFmt(head.topBU.g) + ' MoM' : '',
          head.topBU ? BU_COLORS[head.topBU.bu] : undefined)}
      </div>

      {/* Tren + rata-rata bergerak */}
      <div style={S.card}>
        <div style={S.cardTitle}><Dot color="rgb(var(--brand-600))" />{L('Tren biaya bulanan + rata-rata 3 bln', 'Monthly cost trend + 3-mo avg')}</div>
        <div style={{ height: 280, position: 'relative' }}>
          <Line data={{
            labels: trend.labels,
            datasets: [
              { label: L('Total', 'Total'), data: trend.totals, borderColor: '#1a73e8', backgroundColor: '#1a73e833', borderWidth: 2, fill: true, tension: 0.25, pointRadius: 3 },
              { label: L('Rata-rata 3 bln', '3-mo avg'), data: trend.ma, borderColor: '#b45309', borderWidth: 2, borderDash: [5, 4], fill: false, tension: 0.25, pointRadius: 0 },
            ],
          }} options={{
            ...CHART_OPTS_BASE, maintainAspectRatio: false,
            scales: {
              x: { grid: { color: '#e5e7eb' }, ticks: { color: '#6b7280', font: { size: 11 } } },
              y: { grid: { color: '#e5e7eb' }, ticks: { color: '#6b7280', callback: v => fmtK(v), font: { size: 11 } } },
            },
          }} />
        </div>
      </div>

      {/* Top movers */}
      <div style={S.card}>
        <div style={S.cardTitle}><Dot color="rgb(var(--brand-600))" />
          {L('Top movers — produk', 'Top movers — products')}
          {fullPair.cur && fullPair.prev && (
            <span style={{ fontSize: 11, fontWeight: 400, color: '#9ca3af' }}>
              &nbsp;({pLabel(fullPair.cur)} {L('vs', 'vs')} {pLabel(fullPair.prev)} · {L('bulan penuh', 'full months')})
            </span>
          )}
        </div>
        {movers.length ? (
          <TableWrap>
            <thead><tr>
              <Th left>{L('Produk', 'Product')}</Th>
              <Th>{L('Sebelum', 'Prev')}</Th>
              <Th>{L('Sekarang', 'Current')}</Th>
              <Th>Δ USD</Th>
              <Th>Δ %</Th>
            </tr></thead>
            <tbody>
              {movers.map(m => {
                const open = openMover === m.k;
                const det = moverDetail[m.k];
                return (
                  <React.Fragment key={m.k}>
                    <tr onClick={() => setOpenMover(open ? null : m.k)} style={{ borderBottom: '1px solid #f0ede8', cursor: 'pointer' }}
                      title={L('Klik untuk rincian per usage type & BU', 'Click for usage-type & BU breakdown')}>
                      <Td left>
                        <span style={{ display: 'inline-block', width: 12, color: '#9ca3af', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}>▸</span>
                        {' '}{m.k}
                      </Td>
                      <Td>{fmt(m.prev)}</Td>
                      <Td>{fmt(m.cur)}</Td>
                      <Td color={m.delta > 0 ? '#dc2626' : '#15803d'} bold>{(m.delta > 0 ? '+' : '−') + fmt(m.delta)}</Td>
                      <Td color={pctColor(m.pct)}>{pctFmt(m.pct)}</Td>
                    </tr>
                    {open && (
                      <tr>
                        <td colSpan={5} style={{ background: '#faf9f7', padding: '10px 14px', borderBottom: '1px solid #f0ede8' }}>
                          {det && (det.ut.length || det.bu.length) ? (
                            <div style={{ display: 'grid', gap: 14 }}>
                              <BreakdownSub title={L('Per Usage Type', 'By Usage Type')} rows={det.ut} kind="ut" L={L} pctFmt={pctFmt} pctColor={pctColor} />
                              <BreakdownSub title={L('Per Business Unit', 'By Business Unit')} rows={det.bu} kind="bu" L={L} pctFmt={pctFmt} pctColor={pctColor} />
                            </div>
                          ) : (
                            <div style={{ fontSize: 12, color: '#9ca3af' }}>{L('Tidak ada rincian (usage_month tak memuat produk ini).', 'No breakdown (usage_month has no rows for this product).')}</div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </TableWrap>
        ) : <div style={{ color: '#9ca3af', fontSize: 12, padding: 12 }}>{L('Butuh ≥2 bulan penuh untuk membandingkan.', 'Need ≥2 full months to compare.')}</div>}
      </div>

      {/* Pareto konsentrasi */}
      <div style={S.card}>
        <div style={S.cardTitle}><Dot color="rgb(var(--brand-600))" />{L('Konsentrasi biaya (Pareto)', 'Cost concentration (Pareto)')}</div>
        <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 12 }}>
          {pareto.total > 0 && (
            <>{L('Sekitar', 'About')} <strong>{pareto.n80}</strong> {L('dari', 'of')} {pareto.total} {L('produk menyumbang ~80% total biaya.', 'products drive ~80% of total cost.')}</>
          )}
        </div>
        <div style={{ height: 300, position: 'relative' }}>
          <Bar data={{
            labels: pareto.rows.map(r => r.k),
            datasets: [
              { type: 'bar', label: L('Biaya', 'Cost'), data: pareto.rows.map(r => r.v), backgroundColor: '#1a73e8cc', borderColor: '#1a73e8', borderWidth: 1, borderRadius: 3, yAxisID: 'y', order: 2 },
              { type: 'line', label: L('Kumulatif %', 'Cumulative %'), data: pareto.rows.map(r => r.cumPct), borderColor: '#b45309', borderWidth: 2, pointRadius: 3, yAxisID: 'y1', order: 1, tension: 0.2 },
            ],
          }} options={{
            ...CHART_OPTS_BASE, maintainAspectRatio: false,
            scales: {
              x: { grid: { display: false }, ticks: { color: '#6b7280', font: { size: 10 }, maxRotation: 60, minRotation: 40 } },
              y: { position: 'left', grid: { color: '#e5e7eb' }, ticks: { color: '#6b7280', callback: v => fmtK(v), font: { size: 11 } } },
              y1: { position: 'right', min: 0, max: 100, grid: { drawOnChartArea: false }, ticks: { color: '#b45309', callback: v => v + '%', font: { size: 11 } } },
            },
          }} />
        </div>
      </div>

      {/* Deteksi anomali */}
      <div style={S.card}>
        <div style={S.cardTitle}><Dot color="#dc2626" />{L('Deteksi anomali (lonjakan biaya)', 'Anomaly detection (cost spikes)')}</div>
        {anomalies.length ? (
          <div style={{ display: 'grid', gap: 8 }}>
            {anomalies.map((a, idx) => (
              <div key={a.dim + a.name + a.ym + idx} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                background: '#fff1f2', border: '1px solid #fecdd3', borderRadius: 8, padding: '10px 14px',
              }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a1a' }}>
                    {a.name}
                    <span style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', marginLeft: 6 }}>· {a.dim} · {a.label}</span>
                  </div>
                  <div style={{ fontSize: 11, color: '#9ca3af' }}>
                    {L('vs rata-rata 6 bln', 'vs 6-mo avg')} {fmtK(a.base)} · Δ +{fmt(a.delta)}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontFamily: '"JetBrains Mono",monospace', fontSize: 15, fontWeight: 700, color: '#dc2626' }}>{fmt(a.cur)}</div>
                  <div style={{ fontSize: 11, color: '#dc2626', fontWeight: 700 }}>{a.ratio.toFixed(1)}× ({pctFmt(a.pct)})</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ color: '#15803d', fontSize: 12, padding: 12 }}>
            ✓ {L('Tidak ada lonjakan per service/BU (>1.4× rata-rata 6 bln & kenaikan ≥ $100).', 'No per-service/BU spikes (>1.4× the 6-mo average & ≥ $100 increase).')}
          </div>
        )}
      </div>

      {/* Pergeseran mix BU */}
      <div style={S.card}>
        <div style={S.cardTitle}><Dot color="rgb(var(--brand-600))" />{L('Pergeseran komposisi BU (%)', 'BU mix shift (%)')}</div>
        <div style={{ height: 280, position: 'relative' }}>
          <Bar data={mix} options={{
            ...CHART_OPTS_BASE, maintainAspectRatio: false,
            scales: {
              x: { stacked: true, grid: { display: false }, ticks: { color: '#6b7280', font: { size: 11 } } },
              y: { stacked: true, min: 0, max: 100, grid: { color: '#e5e7eb' }, ticks: { color: '#6b7280', callback: v => v + '%', font: { size: 11 } } },
            },
          }} />
        </div>
      </div>

      {/* Unit economics */}
      <div style={S.card}>
        <div style={S.cardTitle}><Dot color="rgb(var(--brand-600))" />{L('Unit economics (USD per unit pemakaian)', 'Unit economics (USD per usage unit)')}</div>
        {unit.hasQty ? (
          <TableWrap>
            <thead><tr>
              <Th left>{L('Usage type', 'Usage type')}</Th>
              <Th>{L('Total biaya', 'Total cost')}</Th>
              <Th>{L('Total qty', 'Total qty')}</Th>
              <Th>USD / unit</Th>
            </tr></thead>
            <tbody>
              {unit.rows.map(u => (
                <tr key={u.k} style={{ borderBottom: '1px solid #f0ede8' }}>
                  <Td left>{u.label}</Td>
                  <Td>{fmt(u.usd)}</Td>
                  <Td>{fmtQty(u.qty)}</Td>
                  <Td bold>{'$' + u.rate.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: u.rate < 1 ? 6 : 2 })}</Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        ) : (
          <div style={{ color: '#9ca3af', fontSize: 12, padding: 12 }}>
            {L('Data kuantitas pemakaian (UsageQty) tidak tersedia/valid untuk periode ini, jadi unit economics tidak dapat dihitung.',
              'Usage quantity (UsageQty) is unavailable/invalid for this period, so unit economics cannot be computed.')}
          </div>
        )}
      </div>
    </div>
  );
}

// ── MAIN EXPORT ───────────────────────────────────────────────────────────────
const TABS = [
  { id:'overview',     emoji:'📊', labelKey:'awsc.tab.overview'     },
  { id:'savings',      emoji:'💰', labelId:'Savings Plans', labelEn:'Savings Plans' },
  { id:'consolidated', emoji:'🔀', labelKey:'awsc.tab.consolidated' },
  { id:'monthly',      emoji:'📅', labelKey:'awsc.tab.monthly'      },
  { id:'products',     emoji:'📦', labelKey:'awsc.tab.products'     },
  { id:'usage',        emoji:'🔍', labelKey:'awsc.tab.usage'        },
  { id:'daily',        emoji:'📆', labelKey:'awsc.tab.daily'        },
  { id:'analytics',    emoji:'📈', labelId:'Analitik', labelEn:'Analytics' },
];

/**
 * Props:
 *   token  {string}  — JWT dari login (req'd)
 *   style  {object}  — override style container (optional)
 */
export default function AwsCostDashboard({ token, style={}, onDataChange, dataLoader }) {
  const [costAccts, setCostAccts] = useState([]);
  useEffect(() => {
    fetch('/api/aws/accounts', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json()).then((d) => setCostAccts(d.accounts || [])).catch(() => setCostAccts([]));
  }, [token]);
  const { t, lang } = useLang();
  const [searchParams] = useSearchParams();
  // Diturunkan dari TABS (sumber kebenaran tab yang benar-benar dirender), BUKAN
  // ditulis ulang sebagai literal. Versi lama adalah salinan tangan berisi 6 id
  // sementara TABS punya 7 — 'analytics' tertinggal, sehingga deep link
  // ?tab=analytics ditolak validasi dan tab itu tidak bisa dicari sama sekali.
  const TAB_IDS = TABS.map(tb => tb.id);
  const [activeTab, setActiveTab] = useState(() => {
    const p = searchParams.get('tab');
    return (p && TAB_IDS.includes(p)) ? p : 'overview';
  });
  // Initializer di atas hanya jalan sekali; halaman ini tidak pernah di-unmount.
  // Hook ini yang membuat deep link ?tab= tetap bekerja pada kunjungan kedua dst.
  useTabDeepLink('aws_cost', (p) => TAB_IDS.includes(p), setActiveTab);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState(null);
  const [availableYears,setAvailableYears]= useState([]);
  const [selectedYears, setSelectedYears] = useState(new Set());
  const [data, setData] = useState({
    yearly_bu:[], monthly_all:[], prod_month:[], usage_month:[], daily_usage:[],
  });

  // Simpan selected years sebagai string untuk comparison (hindari Set reference issue)
  const selectedYearsKey = useRef('');
  const prevDataKey      = useRef(null);
  const mountedRef       = useRef(false);

  // Satu fungsi load — stabil dengan useRef
  const loadDataFn = useRef(null);
  loadDataFn.current = async (years) => {
    // Mode normal butuh JWT (token); mode share memakai dataLoader (tanpa JWT).
    if (!token && !dataLoader) return;
    setLoading(true); setError(null);
    try {
      const yrs    = years ? [...years] : [];
      const result = dataLoader ? await dataLoader(yrs) : await loadDashboardData(token, yrs);
      applyAllocProfile(result && result.alloc);
      setData(result);
      if (result.availableYears && result.availableYears.length) {
        setAvailableYears(result.availableYears);
        setSelectedYears(prev => {
          if (prev.size === 0) {
            const latest = result.availableYears[result.availableYears.length - 1];
            selectedYearsKey.current = String(latest);
            return new Set([latest]);
          }
          const valid = result.availableYears;
          const next  = new Set([...prev].filter(y => valid.includes(y)));
          return next.size ? next : new Set([valid[valid.length - 1]]);
        });
      }
    } catch(e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const loadData = useCallback((years) => loadDataFn.current(years), []);

  // Initial load — SEKALI saat mount
  useEffect(() => {
    mountedRef.current = true;
    loadDataFn.current([]);
  }, []); // eslint-disable-line

  // Reload saat selectedYears berubah — bandingkan ISI, bukan referensi
  useEffect(() => {
    if (!mountedRef.current) return;
    const newKey = [...selectedYears].sort().join(',');
    if (!newKey) return;                          // skip saat kosong
    if (newKey === selectedYearsKey.current) return; // skip jika tidak berubah
    selectedYearsKey.current = newKey;
    loadDataFn.current(selectedYears);
  }, [selectedYears]); // eslint-disable-line

  // Kirim data ke AI panel
  useEffect(() => {
    if (!onDataChange || loading || !data.yearly_bu.length) return;
    const key = data.yearly_bu.length + '_' + [...selectedYears].sort().join(',');
    if (prevDataKey.current === key) return;
    prevDataKey.current = key;
    onDataChange({
      yearly_bu:     data.yearly_bu,
      monthly_all:   data.monthly_all,
      prod_month:    data.prod_month,
      availableYears,
      selectedYears: [...selectedYears],
    });
  }, [data.yearly_bu.length, loading]); // eslint-disable-line

  const isEmpty = !loading && data.yearly_bu.length === 0;
  // Hanya tampilkan spinner besar saat load PERTAMA (belum ada data).
  // Saat ganti tahun, data lama tetap tampil → tidak berkedip.
  const firstLoad = loading && data.yearly_bu.length === 0;
  const refreshing = loading && data.yearly_bu.length > 0;

  return (
    <div style={{ fontFamily:"'Inter',sans-serif", background:'#f5f5f0',
                  minHeight:'100vh', ...style }}>

      <style>{`
        .aws-kpi-row { grid-template-columns: repeat(${CONS_BUS.length},1fr) !important; }
        @media(max-width:900px){
          .aws-cons-charts { grid-template-columns: 1fr !important; }
          .aws-an-kpi { grid-template-columns: repeat(2,1fr) !important; }
        }
        @media(max-width:600px){
          .aws-kpi-row { grid-template-columns: repeat(${CONS_BUS.length > 3 ? 2 : CONS_BUS.length},1fr) !important; gap:8px !important; }
          .aws-kpi-card { padding:12px 10px !important; }
          .aws-kpi-val  { font-size:15px !important; }
          .aws-kpi-oftotal { display:none !important; }
          .aws-tabbar {
            flex-wrap: nowrap !important;
            overflow-x: auto !important;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
          }
          .aws-tabbar::-webkit-scrollbar { display:none; }
          .aws-tabbar > button { flex: 0 0 auto; }
          .aws-cons-kpi { grid-template-columns: repeat(${CONS_BUS.length > 3 ? 2 : CONS_BUS.length},1fr) !important; gap:8px !important; }
          .aws-cons-card {
            flex-direction: column !important;
            align-items: flex-start !important;
            padding: 12px 10px !important;
            gap: 4px;
          }
          .aws-cons-val { font-size:13px !important; }
          .aws-cons-pct { font-size:16px !important; }
          .aws-cons-alloc { display:none !important; }
          .aws-an-kpi { grid-template-columns: repeat(2,1fr) !important; gap:8px !important; }
        }
        @keyframes awsSpin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @keyframes awsPulse{ 0%,100%{opacity:1} 50%{opacity:.6} }
      `}</style>

      <div style={{ maxWidth:1400, margin:'0 auto', padding:'24px 20px' }}>

        {/* Header */}
        <div style={{ background:"linear-gradient(135deg, rgb(var(--brand-600)) 0%, rgb(var(--brand-400)) 100%)", borderRadius:14, padding:'16px 22px', marginBottom:18,
                      display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:12,
                      boxShadow:'0 2px 8px rgba(0,0,0,0.08)' }}>
          <div>
            <h1 style={{ fontSize:20, fontWeight:800, color:'#fff', margin:0, letterSpacing:'-0.4px' }}>AWS Cost Explorer</h1>
            <p style={{ fontSize:12, color:'rgba(255,255,255,0.7)', margin:'3px 0 0' }}>Cost by business unit, service and usage type</p>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
          {token && isRatioMode() && <AllocSettingsButton token={token} />}
          <RefreshFromAwsButton endpoint="/api/aws/fetch-aws" kind="cost" />
          {costAccts.length > 1 && <span style={{ fontSize:12, color:'rgba(255,255,255,0.75)' }}>AWS account</span>}
          {costAccts.length > 1 && (<select
            value={getCostAcct()}
            onChange={(e) => switchCostAcct(e.target.value)}
            style={{ padding:'5px 10px', borderRadius:8, border:'1px solid rgba(255,255,255,0.35)', background:'rgba(255,255,255,0.12)', color:'#fff', fontSize:13, fontWeight:600 }}
          >
            {costAccts.map((a) => <option key={a.key} value={a.key} style={{ color: '#111' }}>{a.label}</option>)}
          </select>)}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div style={{ background:'#fff1f2', border:'1px solid #fecdd3', borderRadius:10,
                        padding:'12px 16px', marginBottom:18, color:'#dc2626', fontSize:13 }}>
            ⚠ {error}
          </div>
        )}

        {/* Loading (hanya saat load pertama) */}
        {firstLoad && (
          <div style={{ textAlign:'center', padding:'60px 20px', color:'#6b7280', fontSize:14 }}>
            <div style={{ fontSize:32, marginBottom:10,
                          animation:'awsSpin 1s linear infinite', display:'inline-block' }}>⟳</div>
            <div>{t('awsc.loading')}</div>
          </div>
        )}

        {/* Empty */}
        {!loading && isEmpty && !error && (
          <div style={{ textAlign:'center', padding:'80px 24px',
                        background:'#fff', borderRadius:16,
                        border:'2px dashed #d4cfc9' }}>
            <div style={{ fontSize:52, marginBottom:14 }}>☁</div>
            <div style={{ fontSize:18, fontWeight:700, color:'#1a1a1a', marginBottom:8 }}>
              {t('awsc.empty')}
            </div>
            <div style={{ fontSize:13, color:'#6b7280', marginBottom:20 }}>
              {t('awsc.emptyHint1')}
            </div>
            <div style={{ display:'inline-block', background:'#f0ede8', borderRadius:10,
                          padding:'12px 20px', fontSize:12, color:'#6b7280',
                          border:'1px solid #e2ddd8', fontFamily:'"JetBrains Mono",monospace',
                          textAlign:'left' }}>
              {t('awsc.requiredCols')}<br/>
              <strong style={{ color:'#1a1a1a' }}>Usage Type · Product Code · Month · Business Unit · USD</strong>
            </div>
          </div>
        )}

        {/* Dashboard */}
        {!firstLoad && !isEmpty && (
          <div>
            <div style={{
              opacity: refreshing ? 0.5 : 1,
              transition: 'opacity .2s ease',
              pointerEvents: refreshing ? 'none' : 'auto',
            }}>
              <TotalBanner yearlyBU={data.yearly_bu} selectedYears={selectedYears} />
              <KpiCards    yearlyBU={data.yearly_bu} selectedYears={selectedYears} />
            </div>

            {/* Tab bar + Year selector */}
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                          flexWrap:'wrap', gap:10, marginBottom:18 }}>
              {/* Tabs */}
              <div className="aws-tabbar" style={{ display:'flex', gap:4,
                            background:'#fff', border:'1px solid #e2ddd8',
                            borderRadius:10, padding:4,
                            boxShadow:'0 1px 3px rgba(0,0,0,0.06)', flexWrap:'wrap' }}>
                {TABS.map(tab=>(
                  <button key={tab.id} onClick={()=>setActiveTab(tab.id)}
                    style={{ padding:'7px 16px', borderRadius:8, border:'none',
                      background: activeTab===tab.id?'rgb(var(--brand-600))':'transparent',
                      color: activeTab===tab.id?'#fff':'#6b7280',
                      fontWeight: activeTab===tab.id?700:400,
                      fontSize:12, cursor:'pointer', fontFamily:'inherit',
                      transition:'all .15s', whiteSpace:'nowrap' }}>
                    {tab.emoji} {tab.labelKey ? t(tab.labelKey) : (lang === 'en' ? tab.labelEn : tab.labelId)}
                  </button>
                ))}
              </div>

              {/* Year selector */}
              <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                {availableYears.length > 1 && (() => {
                  const allActive = selectedYears.size === availableYears.length;
                  return (
                    <button
                      onClick={()=>setSelectedYears(new Set(availableYears))}
                      style={{
                        padding:'5px 14px', borderRadius:20, fontFamily:'inherit',
                        fontSize:12, fontWeight:700, cursor:'pointer', transition:'all .15s',
                        border: allActive?'1.5px solid rgb(var(--brand-600))':'1px solid #d4cfc9',
                        background: allActive?'rgb(var(--brand-600))':'#fff',
                        color: allActive?'#fff':'#6b7280',
                      }}>{t('awsc.allYears')}</button>
                  );
                })()}
                {availableYears.map(y => (
                  <button key={y}
                    onClick={e => {
                      const multi = e.ctrlKey||e.metaKey||e.shiftKey;
                      setSelectedYears(prev => {
                        let next = new Set(prev);
                        if (multi) { next.has(y)?(next.size>1&&next.delete(y)):next.add(y); }
                        else next = new Set([y]);
                        return next;
                      });
                    }}
                    style={{
                      padding:'5px 14px', borderRadius:20, fontFamily:'inherit',
                      fontSize:12, fontWeight:700, cursor:'pointer', transition:'all .15s',
                      border: selectedYears.has(y)?'1.5px solid rgb(var(--brand-600))':'1px solid #d4cfc9',
                      background: selectedYears.has(y)?'rgb(var(--brand-600))':'#fff',
                      color: selectedYears.has(y)?'#fff':'#6b7280',
                    }}>{y}</button>
                ))}
                {selectedYears.size > 1 && (
                  <button
                    onClick={()=>setSelectedYears(new Set([availableYears[availableYears.length-1]]))}
                    style={{ fontSize:11, padding:'3px 10px', borderRadius:20, fontFamily:'inherit',
                      border:'1px solid #d4cfc9', background:'#f0ede8', color:'#6b7280', cursor:'pointer' }}>
                    &#x2715; {t('awsc.reset')}
                  </button>
                )}
                {availableYears.length > 0 &&
                  <span style={{ fontSize:10, color:'#9ca3af' }}>{t('awsc.ctrlMulti')}</span>}
                {refreshing && (
                  <span style={{ fontSize:14, color:'rgb(var(--brand-600))', display:'inline-block',
                                 animation:'awsSpin 1s linear infinite', lineHeight:1 }}>⟳</span>
                )}
              </div>
            </div>

            {/* Tab content */}
            <div style={{
              opacity: refreshing ? 0.5 : 1,
              transition: 'opacity .2s ease',
              pointerEvents: refreshing ? 'none' : 'auto',
            }}>
            {activeTab==='overview' && (
              <div className="aws-charts-grid" style={{ display:'grid',
                gridTemplateColumns:'1fr 1fr', gap:18, marginBottom:20 }}>
                <MonthlyChart monthlyAll={data.monthly_all} selectedYears={selectedYears} />
                <PieChart     yearlyBU={data.yearly_bu}     selectedYears={selectedYears} />
                <YearlyChart  yearlyBU={data.yearly_bu} />
              </div>
            )}
            {activeTab==='savings' && <SavingsPlanPanel embedded />}
            {activeTab==='consolidated' &&
              <ConsolidatedTab monthlyAll={data.monthly_all} selectedYears={selectedYears} />}
            {activeTab==='monthly'  &&
              <MonthlyTab monthlyAll={data.monthly_all} selectedYears={selectedYears} />}
            {activeTab==='products' &&
              <ProductsTab prodMonth={data.prod_month} selectedYears={selectedYears} />}
            {activeTab==='usage'    &&
              <UsageTab usageMonth={data.usage_month} selectedYears={selectedYears} />}
            {activeTab==='daily'    &&
              <DailyUsageTab dailyUsage={data.daily_usage} selectedYears={selectedYears} />}
            {activeTab==='analytics' &&
              <AnalyticsTab monthlyAll={data.monthly_all} prodMonth={data.prod_month}
                            usageMonth={data.usage_month} selectedYears={selectedYears} />}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
